import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  downloadDepositsReportCsv,
  downloadDepositsReportPdf,
  fetchAdminAnalytics,
  fetchPlatformSettings,
  updatePlatformSettings,
} from "../services/adminService";
import {
  assignProjectManager,
  unassignProjectManager,
  blockUser,
  broadcastNotice,
  createAdminStaffUser,
  createAdminProject,
  deleteAdminProjectOverviewPdf,
  extendAdminProject,
  getAdminProjectOverviewPdfSignedUrlByKey,
  listAdminKyc,
  listAdminInvestments,
  listAdminPnl,
  listAdminProjects,
  presignAdminProjectOverviewPdfUpload,
  uploadAdminProjectOverviewPdf,
  uploadAdminProjectOverviewPdfToProject,
  uploadAdminProjectHero,
  deleteAdminProjectHero,
  publishAdminProjectLanding,
  listAdminUsers,
  reinstateUser,
  updateUserPermissions,
  reviewAdminKyc,
  reviewAdminInvestment,
  reviewAdminRedemption,
  reviewAdminPnl,
  updateAdminProject,
  pauseAdminProject,
  closeRedeemedAdminProject,
  resumeAdminProject,
  cancelAdminProject,
  suspendUser,
} from "../services/adminWorkflowService";
import {
  bulkReviewAdminDeposits,
  fetchSuperAdminDepositOverview,
  getAdminDepositScreenshotSignedUrl,
  listAdminDeposits,
  listAdminTransactions,
  listAdminWithdrawals,
  presignAdminWithdrawalProofUpload,
  reviewAdminDeposit,
  getAdminWithdrawalProofSignedUrl,
  reviewAdminWithdrawal,
} from "../services/paymentService";
import { useAuth } from "../store/AuthContext";
import { getAllowedAdminModules, hasAdminModuleAccess } from "../utils/adminPermissions";
import {
  buildLifecycleBuckets,
  buildManagerAssignmentRows,
  isAdminProjectContentLocked,
  isAdminProjectOfferTermsLocked,
  isCompletedProjectStatus,
  isRedeemedOrClosedProjectStatus,
  normalizeProjectStatus,
  projectNameOnly,
  sortProjectsNewestFirst,
} from "../utils/projectViews";
import LockInCountdown from "../components/LockInCountdown";
import DocumentPreviewModal from "../components/DocumentPreviewModal";
import {
  AdminProjectFinancialDataCells,
  AdminProjectFinancialHeaderCells,
  usePnlTotalsByProject,
} from "../components/AdminProjectFinancialCells";
import { walletTransactionTypeLabel } from "../utils/walletTransactionLabels";
import {
  countFilledParagraphs,
  joinDescriptionParagraphs,
  splitDescriptionParagraphs,
} from "../utils/projectDescription";
import { fetchProjectDetails, getProjectOverviewPdfSignedUrl } from "../services/investmentService";

function paginate(rows, page, pageSize) {
  const list = Array.isArray(rows) ? rows : rows?.results && Array.isArray(rows.results) ? rows.results : [];
  const safePage = Math.max(1, page);
  const start = (safePage - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

function unwrapAdminList(data) {
  if (Array.isArray(data)) return data;
  if (data?.results && Array.isArray(data.results)) return data.results;
  return [];
}

function fmtShareQty(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : String(v ?? "—");
}

/** Controls column: Edit (draft/review) vs Update (performance PDF for live projects). */
function adminProjectControlAction(stageKey) {
  if (stageKey === "DRAFT" || stageKey === "REVIEW") {
    return {
      label: "Edit",
      pdfUpdateOnly: false,
      title: "Edit project details, structure, and overview PDF",
    };
  }
  if (["FUNDRAISING", "FUNDED", "TERMINAL"].includes(stageKey)) {
    return {
      label: "Update",
      pdfUpdateOnly: true,
      title: "Upload or replace the project performance PDF for investors",
    };
  }
  return {
    label: "Edit",
    pdfUpdateOnly: false,
    title: "Edit project details",
  };
}

/** Shared column headers for live / redeemed project tables. */
function AdminProjectsMarketTableHead({ actionsLabel = "Controls" }) {
  return (
    <>
      <th className="ops-table__cell--identity ops-table__col--sl">SL.</th>
      <th className="ops-table__cell--identity ops-table__col--code">Project</th>
      <th className="ops-table__cell--identity ops-table__col--particulars">Particulars</th>
      <th className="ops-table__cell--identity ops-table__col--attachments">Attachments</th>
      <th className="ops-table__cell--identity ops-table__col--status">Status</th>
      <th className="ops-table__cell--identity ops-table__col--supply">Share supply</th>
      <th className="ops-table__cell--metrics ops-table__col--return" title="Estimated return per annum">
        Est. return (p.a.)
      </th>
      <AdminProjectFinancialHeaderCells />
      <th className="ops-table__cell--actions">{actionsLabel}</th>
    </>
  );
}

function AdminProjectLifecycleNote({ project }) {
  const st = normalizeProjectStatus(project?.status);
  if (!["LOCK_IN", "MATURED"].includes(st)) return null;
  const unlockIso = project.redemption_unlock_at || project.redemptionUnlockAt;
  const unlock = unlockIso ? new Date(unlockIso) : null;
  const progress =
    project.redemption_progress_pct != null ? String(project.redemption_progress_pct) : null;

  if (project.lock_in_active === true && unlock && !Number.isNaN(unlock.getTime())) {
    return (
      <div className="admin-project-lifecycle">
        <span className="admin-status-chip admin-status-chip--warn">Lock-in active</span>
        <span className="admin-project-lifecycle__meta">
          Unlocks {unlock.toLocaleDateString()}
        </span>
        <LockInCountdown
          unlockIso={unlockIso}
          className="admin-project-lifecycle__countdown"
          variant="block"
        />
      </div>
    );
  }
  if (project.eligible_for_redemption === true) {
    return (
      <div className="admin-project-lifecycle">
        <span className="admin-status-chip admin-status-chip--ok">Eligible for redemption</span>
        {progress != null ? (
          <span className="admin-project-lifecycle__meta">Parent ownership {progress}%</span>
        ) : null}
      </div>
    );
  }
  if (project.lock_in_active === false && unlock && !Number.isNaN(unlock.getTime())) {
    return (
      <div className="admin-project-lifecycle">
        <span className="admin-status-chip admin-status-chip--neutral">Post lock-in</span>
        <span className="admin-project-lifecycle__meta">
          Ended {unlock.toLocaleDateString()}
          {progress != null ? ` · Redemption ${progress}%` : ""}
        </span>
      </div>
    );
  }
  return null;
}

/** Admin API share-cap fields (SSA treasury; cap unchanged when fees settle in shares). */
function adminShareSupplyCell(p) {
  const cap = p?.share_cap_current ?? p?.total_shares;
  const sold = p?.shares_sold_primary ?? p?.shares_sold;
  const available = p?.shares_available;
  const ssaTreasury = p?.ssa_treasury_shares_held;
  const investorHeld = p?.shares_held_investors;
  const ssaTransfer = p?.ssa_transfer_fee_shares;
  const ssaRedeemFee = p?.ssa_redemption_fee_shares;
  const preSsaReduction =
    p?.pre_ssa_cap_reduction_shares ?? p?.legacy_transfer_fee_burned_shares ?? p?.transfer_fee_burned_shares;
  const nominalCap = p?.share_cap_nominal ?? p?.share_cap_before_legacy_burns ?? p?.share_cap_before_transfer_burns;
  if (cap == null && sold == null && available == null) {
    return null;
  }
  return {
    cap: cap ?? "—",
    sold: sold ?? "—",
    available: available ?? "—",
    nominalCap: nominalCap ?? null,
    ssaTreasury: ssaTreasury ?? "0",
    investorHeld: investorHeld ?? null,
    ssaTransfer: ssaTransfer ?? "0",
    ssaRedeemFee: ssaRedeemFee ?? "0",
    preSsaReduction: preSsaReduction ?? "0",
  };
}

function AdminShareSupplySummary({ project }) {
  const s = adminShareSupplyCell(project);
  if (!s) return <span className="muted">—</span>;
  const ssaTfN = Number(s.ssaTransfer);
  const ssaRfN = Number(s.ssaRedeemFee);
  const preSsaN = Number(s.preSsaReduction);
  const nominalN = s.nominalCap != null ? Number(s.nominalCap) : NaN;
  const capN = Number(s.cap);
  const showNominalCap = preSsaN > 0 && Number.isFinite(nominalN) && nominalN > capN;
  const ssaTreasuryN = Number(s.ssaTreasury);
  const investorHeldDisplay =
    s.investorHeld != null
      ? s.investorHeld
      : Number.isFinite(capN) && Number.isFinite(ssaTreasuryN)
        ? (capN - ssaTreasuryN).toFixed(2)
        : null;
  const showHeldEquation =
    investorHeldDisplay != null && Number.isFinite(capN);
  const feeHints = [];
  if (ssaTfN > 0) feeHints.push(`Transfer fees in SSA ${fmtShareQty(s.ssaTransfer)}`);
  if (ssaRfN > 0) feeHints.push(`Redemption fees in SSA ${fmtShareQty(s.ssaRedeemFee)}`);
  if (preSsaN > 0 && !showNominalCap) feeHints.push(`Pre-SSA adjustment −${fmtShareQty(s.preSsaReduction)}`);

  return (
    <div
      className="admin-share-card"
      title="Share cap: investors plus SSA treasury equals authorized cap"
    >
      {showHeldEquation ? (
        <div className="admin-share-card__grid" role="group" aria-label="Share holdings">
          <div className="admin-share-stat">
            <span className="admin-share-stat__label">Investors</span>
            <span className="admin-share-stat__value">{fmtShareQty(investorHeldDisplay)}</span>
          </div>
          <span className="admin-share-card__op" aria-hidden>
            +
          </span>
          <div className="admin-share-stat admin-share-stat--ssa">
            <span className="admin-share-stat__label">SSA</span>
            <span className="admin-share-stat__value">{fmtShareQty(s.ssaTreasury)}</span>
          </div>
          <span className="admin-share-card__op" aria-hidden>
            =
          </span>
          <div className="admin-share-stat admin-share-stat--cap">
            <span className="admin-share-stat__label">Cap</span>
            <span className="admin-share-stat__value">{fmtShareQty(s.cap)}</span>
          </div>
        </div>
      ) : (
        <div className="admin-share-card__solo">
          Cap <strong>{fmtShareQty(s.cap)}</strong>
        </div>
      )}
      <div className="admin-share-card__primary" title="Primary offering — not reduced when shares move to SSA">
        <span>
          Sold <strong>{fmtShareQty(s.sold)}</strong>
        </span>
        <span className="admin-share-card__sep" aria-hidden>
          ·
        </span>
        <span>
          Available <strong>{fmtShareQty(s.available)}</strong>
        </span>
      </div>
      {showNominalCap ? (
        <p className="admin-share-card__hint">Was {fmtShareQty(s.nominalCap)} before pre-SSA fees</p>
      ) : null}
      {feeHints.length > 0 ? <p className="admin-share-card__hint">{feeHints.join(" · ")}</p> : null}
    </div>
  );
}

function AdminProjectAttachmentsCell({ project, onViewPdf }) {
  const hasPdf = Boolean(String(project?.overview_pdf_url || "").trim());
  return (
    <div className="admin-project-attachments">
      <span
        className={`admin-doc-chip${hasPdf ? " admin-doc-chip--ok" : " admin-doc-chip--missing"}`}
      >
        {hasPdf ? "PDF" : "No PDF"}
      </span>
      {hasPdf && onViewPdf ? (
        <button
          type="button"
          className="admin-project-attachments__link"
          onClick={() => onViewPdf(project)}
        >
          View
        </button>
      ) : null}
    </div>
  );
}

function AdminProjectParticularsCell({ project }) {
  return (
    <div className="admin-project-particulars">
      <p className="admin-project-particulars__title">{projectNameOnly(project)}</p>
      <AdminProjectLifecycleNote project={project} />
    </div>
  );
}

function PaginationControls({ page, total, pageSize, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="pagination-bar">
      <button className="btn-secondary" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
        Prev
      </button>
      <span>
        Page {Math.min(page, totalPages)} / {totalPages}
      </span>
      <button className="btn-secondary" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
        Next
      </button>
    </div>
  );
}

function SuccessPopup({ open, title = "Success", leadName = "", message = "", onClose }) {
  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card modal-card-elevated" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="close-btn" aria-label="Close success message" onClick={onClose}>
          ×
        </button>
        <h3>{title}</h3>
        <p className="muted" style={{ margin: "4px 0 8px" }}>
          {leadName ? <><strong>{leadName}</strong> {message}</> : message}
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose}>OK</button>
        </div>
      </div>
    </div>
  );
}

function isoDateOffset(days) {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function createInitialNewProject() {
  return {
    name: "",
    address: "",
    description_p1: "",
    description_p2: "",
    description_p3: "",
    category: "CLINIC",
    total_shares: 100,
    initial_share_price: 100,
    expected_return_pct: 10,
    fundraising_start: isoDateOffset(0),
    fundraising_end: isoDateOffset(30),
    risk_disclaimer: "",
    overview_pdf_url: "",
    video_url: "",
    landing_published: true,
  };
}

function projectInvestorPreviewHref(project) {
  return `/investments?project_id=${encodeURIComponent(project.id)}&preview=1`;
}

function projectLandingPreviewHref(project) {
  const slug = String(project?.landing_slug || "").trim();
  if (slug) return `/p/${encodeURIComponent(slug)}`;
  return `/projects/${encodeURIComponent(project.id)}/about`;
}

function AdminProjectPreviewButtons({ project }) {
  if (!project?.id) return null;
  return (
    <>
      <a
        className="btn-secondary compact-btn"
        href={projectInvestorPreviewHref(project)}
        target="_blank"
        rel="noopener noreferrer"
        title="See how this project appears in the investor Shares Management panel"
      >
        Preview investor
      </a>
      <a
        className="btn-secondary compact-btn"
        href={projectLandingPreviewHref(project)}
        target="_blank"
        rel="noopener noreferrer"
        title="See the public landing page (or authenticated preview if not published yet)"
      >
        Preview landing
      </a>
    </>
  );
}

export default function AdminControlPage() {
  const COUNTRY_CODE_OPTIONS = [
    { value: "+880", label: "+880 (Bangladesh)" },
    { value: "+93", label: "+93 (Afghanistan)" },
    { value: "+61", label: "+61 (Australia)" },
    { value: "+43", label: "+43 (Austria)" },
    { value: "+973", label: "+973 (Bahrain)" },
    { value: "+32", label: "+32 (Belgium)" },
    { value: "+55", label: "+55 (Brazil)" },
    { value: "+86", label: "+86 (China)" },
    { value: "+45", label: "+45 (Denmark)" },
    { value: "+20", label: "+20 (Egypt)" },
    { value: "+33", label: "+33 (France)" },
    { value: "+49", label: "+49 (Germany)" },
    { value: "+30", label: "+30 (Greece)" },
    { value: "+852", label: "+852 (Hong Kong)" },
    { value: "+91", label: "+91 (India)" },
    { value: "+62", label: "+62 (Indonesia)" },
    { value: "+98", label: "+98 (Iran)" },
    { value: "+964", label: "+964 (Iraq)" },
    { value: "+353", label: "+353 (Ireland)" },
    { value: "+39", label: "+39 (Italy)" },
    { value: "+81", label: "+81 (Japan)" },
    { value: "+962", label: "+962 (Jordan)" },
    { value: "+254", label: "+254 (Kenya)" },
    { value: "+965", label: "+965 (Kuwait)" },
    { value: "+960", label: "+960 (Maldives)" },
    { value: "+977", label: "+977 (Nepal)" },
    { value: "+31", label: "+31 (Netherlands)" },
    { value: "+64", label: "+64 (New Zealand)" },
    { value: "+234", label: "+234 (Nigeria)" },
    { value: "+47", label: "+47 (Norway)" },
    { value: "+92", label: "+92 (Pakistan)" },
    { value: "+63", label: "+63 (Philippines)" },
    { value: "+974", label: "+974 (Qatar)" },
    { value: "+44", label: "+44 (UK)" },
    { value: "+971", label: "+971 (UAE)" },
    { value: "+966", label: "+966 (Saudi Arabia)" },
    { value: "+65", label: "+65 (Singapore)" },
    { value: "+27", label: "+27 (South Africa)" },
    { value: "+82", label: "+82 (South Korea)" },
    { value: "+94", label: "+94 (Sri Lanka)" },
    { value: "+46", label: "+46 (Sweden)" },
    { value: "+41", label: "+41 (Switzerland)" },
    { value: "+66", label: "+66 (Thailand)" },
    { value: "+90", label: "+90 (Turkey)" },
    { value: "+1", label: "+1 (USA/Canada)" },
    { value: "+84", label: "+84 (Vietnam)" },
    { value: "+60", label: "+60 (Malaysia)" },
  ];
  const NATIONALITY_OPTIONS = [
    "Bangladeshi",
    "Afghan",
    "Algerian",
    "Argentine",
    "Australian",
    "Austrian",
    "Bahraini",
    "Belgian",
    "Brazilian",
    "British",
    "Canadian",
    "Chinese",
    "Danish",
    "Dutch",
    "Egyptian",
    "Filipino",
    "French",
    "German",
    "Greek",
    "Hong Konger",
    "Indian",
    "Indonesian",
    "Iranian",
    "Iraqi",
    "Irish",
    "Italian",
    "Japanese",
    "Jordanian",
    "Kenyan",
    "Kuwaiti",
    "Malaysian",
    "Maldivian",
    "New Zealander",
    "Nigerian",
    "Norwegian",
    "Pakistani",
    "Nepali",
    "Qatari",
    "Saudi",
    "Singaporean",
    "South African",
    "South Korean",
    "Sri Lankan",
    "American",
    "Swedish",
    "Swiss",
    "Thai",
    "Turkish",
    "Emirati",
    "Vietnamese",
    "Other",
  ];
  const STAFF_ROLE_OPTIONS = [
    { value: "ASSET_MANAGER", label: "Asset Manager" },
    { value: "ADMIN", label: "General Admin" },
    { value: "ACCOUNTANT", label: "Accountant" },
    { value: "MARKETING", label: "Marketing" },
  ];
  const ADMIN_PERMISSION_OPTIONS = [
    { key: "DASHBOARD", label: "Admin Dashboard" },
    { key: "CONTROL", label: "Admin Control" },
    { key: "OPERATIONS", label: "Admin Operations" },
    { key: "PAYMENTS", label: "Admin Payments" },
  ];
  const TABS = [
    "OVERVIEW",
    "USERS",
    "ASSET_MANAGERS",
    "PROJECTS",
    "WALLET_TXNS",
    "KYC",
    "PROFIT",
    "REDEMPTIONS",
    "NOTICE",
    "SETTINGS",
  ];
  const TAB_LABELS = {
    OVERVIEW: "Overview",
    USERS: "User Management",
    ASSET_MANAGERS: "Asset Managers",
    WALLET_TXNS: "Wallet & Transactions",
    KYC: "KYC Management",
    PROJECTS: "Projects",
    PROFIT: "Profit Management",
    REDEMPTIONS: "Redemptions",
    NOTICE: "Notice Management",
    SETTINGS: "Settings",
  };
  const TAB_ICONS = {
    OVERVIEW: "📊",
    USERS: "👥",
    ASSET_MANAGERS: "🧑‍💼",
    PROJECTS: "🏗️",
    WALLET_TXNS: "💳",
    KYC: "🪪",
    PROFIT: "💰",
    REDEMPTIONS: "🏧",
    NOTICE: "📣",
    SETTINGS: "⚙️",
  };
  const TAB_META = {
    OVERVIEW: { tone: "ops", hint: "Operational shortcuts and pending workload. Full KPIs are on the Admin Dashboard." },
    USERS: { tone: "users", hint: "Search users, review status, and manage suspend/block/reinstate actions." },
    ASSET_MANAGERS: { tone: "staff", hint: "Create staff accounts and assign role-based module permissions." },
    PROJECTS: { tone: "projects", hint: "Create and control project lifecycle, files, and assignment flow." },
    WALLET_TXNS: { tone: "finance", hint: "Review deposits, withdrawals, and wallet ledger activity." },
    KYC: { tone: "kyc", hint: "Review identity documents. Approved KYC is required before investors can buy shares (and for wallet BDT withdrawals)." },
    PROFIT: { tone: "finance", hint: "Validate P&L submissions from asset managers for funded projects." },
    REDEMPTIONS: {
      tone: "finance",
      hint: "Approve or reject investor sell-to-treasury requests; audit past redemptions and pending secondary transfers.",
    },
    NOTICE: { tone: "notice", hint: "Broadcast operational announcements to target audiences." },
    SETTINGS: { tone: "settings", hint: "Update platform-wide configuration values safely." },
  };
  /** Matches API module guards on admin workflow / payments endpoints */
  const TAB_REQUIRED_MODULE = {
    OVERVIEW: "CONTROL",
    ASSET_MANAGERS: "CONTROL",
    PROJECTS: "OPERATIONS",
    WALLET_TXNS: "PAYMENTS",
    KYC: "CONTROL",
    PROFIT: "OPERATIONS",
    REDEMPTIONS: "OPERATIONS",
    NOTICE: "CONTROL",
    SETTINGS: "CONTROL",
  };
  const location = useLocation();
  const navigate = useNavigate();
  const usersOnlyMode = location.pathname === "/admin/users";
  const { user, logout } = useAuth();
  const visibleTabs = useMemo(() => {
    if (usersOnlyMode) return ["USERS"];
    return TABS.filter((t) => t !== "USERS" && hasAdminModuleAccess(user, TAB_REQUIRED_MODULE[t]));
  }, [usersOnlyMode, user]);
  const [analytics, setAnalytics] = useState(null);
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [superDepositOverview, setSuperDepositOverview] = useState(null);
  const [kycItems, setKycItems] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [pnlItems, setPnlItems] = useState([]);
  const [activeTab, setActiveTab] = useState("OVERVIEW");
  const [adminTheme, setAdminTheme] = useState(() => {
    try {
      const saved = localStorage.getItem("adminControlTheme");
      return saved === "contrast" ? "contrast" : "soft";
    } catch (_err) {
      return "soft";
    }
  });
  const [newProject, setNewProject] = useState(() => createInitialNewProject());
  const [customProjectCategory, setCustomProjectCategory] = useState("");
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [projectCreateError, setProjectCreateError] = useState("");
  const [projectCreateSubmitting, setProjectCreateSubmitting] = useState(false);
  const [projectEditModal, setProjectEditModal] = useState({
    open: false,
    projectId: "",
    lifecycleLocked: false,
    offerTermsLocked: false,
    readOnly: false,
    pdfUpdateOnly: false,
    form: {
      name: "",
      address: "",
      description_p1: "",
      description_p2: "",
      description_p3: "",
      category: "CLINIC",
      total_shares: 0,
      initial_share_price: 0,
      expected_return_pct: 0,
      fundraising_start: "",
      fundraising_end: "",
      risk_disclaimer: "",
      overview_pdf_url: "",
      video_url: "",
      landing_published: false,
      landing_slug: "",
      landing_hero_image_url: "",
    },
  });
  const [landingModal, setLandingModal] = useState({
    open: false,
    projectId: "",
    projectName: "",
    saving: false,
    form: {
      landing_headline: "",
      landing_subline: "",
      landing_highlights_text: "",
      landing_terms_notes: "",
      landing_published: false,
      landing_slug: "",
      landing_hero_image_url: "",
      video_url: "",
    },
  });
  const [projectStageModal, setProjectStageModal] = useState({
    open: false,
    projectId: "",
    currentStage: "DRAFT",
    stage: "DRAFT",
    allowedStages: [],
  });
  const [message, setMessageState] = useState("");
  const [messageIsError, setMessageIsError] = useState(false);
  const setMessage = (text, isError) => {
    const value = text || "";
    if (isError === undefined) {
      isError = /timeout of \d+ms exceeded|timed out\.|Failed to |Could not |failed\.|not configured|Invalid |required\.|Export failed|PDF export failed|restricted for your/i.test(
        String(value)
      );
    }
    setMessageIsError(Boolean(isError));
    setMessageState(value);
  };
  const [userFilter, setUserFilter] = useState({ q: "", status: "", kyc_status: "" });
  const [assetManagerForm, setAssetManagerForm] = useState({
    role: "ASSET_MANAGER",
    full_name: "",
    email: "",
    country_code: "+880",
    phone_number: "",
    nationality: "Bangladeshi",
    password: "",
  });
  const [assetManagerSubmitting, setAssetManagerSubmitting] = useState(false);
  const [assetManagerSubmitAttempted, setAssetManagerSubmitAttempted] = useState(false);
  const [assetManagerToast, setAssetManagerToast] = useState("");
  const [assetManagerToastLeadName, setAssetManagerToastLeadName] = useState("");
  const [assetManagerSubmitError, setAssetManagerSubmitError] = useState("");
  const [showAssetManagerPassword, setShowAssetManagerPassword] = useState(false);
  const [projectFilter, setProjectFilter] = useState("ALL");
  const [txnFilter, setTxnFilter] = useState({ direction: "", type: "" });
  const [managerAssignments, setManagerAssignments] = useState({});
  const [noticeForm, setNoticeForm] = useState({
    title: "",
    message: "",
    audience: "INVESTORS",
    channel: "IN_APP",
  });
  const [pages, setPages] = useState({
    users: 1,
    assetManagers: 1,
    deposits: 1,
    withdrawals: 1,
    txns: 1,
    kyc: 1,
    projects: 1,
    assignments: 1,
    assignedManagers: 1,
    investments: 1,
    pastRedemptions: 1,
    investmentsOther: 1,
    pnl: 1,
    pnlHistory: 1,
    redeemedClosed: 1,
  });
  const [pnlHistoryProjectFilter, setPnlHistoryProjectFilter] = useState("");
  const [rejectModal, setRejectModal] = useState({
    open: false,
    kind: "",
    id: "",
    depositIds: [],
    investmentTxType: "",
    reason: "",
    title: "",
  });
  const [depositApproveModal, setDepositApproveModal] = useState({
    open: false,
    kind: "",
    id: "",
    depositIds: [],
    amountLabel: "",
    remark: "",
    submitting: false,
  });
  const [redemptionApproveModal, setRedemptionApproveModal] = useState({
    open: false,
    id: "",
    ref: "",
    shares: "",
    approved_shares: "",
    admin_notes: "",
    submitting: false,
  });
  const [permissionModal, setPermissionModal] = useState({
    open: false,
    userId: "",
    userName: "",
    permissions: [],
  });
  const [selectedDepositIds, setSelectedDepositIds] = useState([]);
  const [withdrawalProofRefs, setWithdrawalProofRefs] = useState({});
  const [withdrawalProofBusy, setWithdrawalProofBusy] = useState({});
  const [depositDocPreview, setDepositDocPreview] = useState({
    open: false,
    loading: false,
    url: "",
    error: "",
    title: "Deposit proof",
    subtitle: "",
    authRequired: false,
    contentHint: "",
  });
  const [projectOverviewUploadBusy, setProjectOverviewUploadBusy] = useState(false);
  const [projectOverviewDeleteBusy, setProjectOverviewDeleteBusy] = useState(false);
  const [projectOverviewAttachBusy, setProjectOverviewAttachBusy] = useState(false);
  const [projectEditPendingPdfFile, setProjectEditPendingPdfFile] = useState(null);
  const [projectEditPdfUploadProgress, setProjectEditPdfUploadProgress] = useState(null);
  const createProjectOverviewKeyRef = useRef("");
  const createProjectHeroFileRef = useRef(null);
  const editProjectOverviewKeyRef = useRef("");

  const setPage = (key, value) => setPages((prev) => ({ ...prev, [key]: value }));
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const getErrorMessage = (err, fallback) => {
    const code = err?.code;
    const net = typeof err?.message === "string" ? err.message.trim() : "";
    if (
      code === "ECONNABORTED"
      || /timeout of \d+ms exceeded/i.test(net)
      || /network error/i.test(net)
    ) {
      return "Request timed out. Please try again.";
    }
    const data = err?.response?.data;
    if (!data) {
      if (net && net !== "Authentication required.") return net;
      return fallback;
    }
    if (typeof data === "string") return data;
    if (typeof data?.detail === "string" && data.detail.trim()) return data.detail;
    if (Array.isArray(data?.detail) && data.detail.length > 0) return String(data.detail[0]);
    if (typeof data === "object") {
      for (const [field, value] of Object.entries(data)) {
        if (Array.isArray(value) && value.length > 0) {
          return `${field.replaceAll("_", " ")}: ${String(value[0])}`;
        }
        if (typeof value === "string" && value.trim()) {
          return `${field.replaceAll("_", " ")}: ${value}`;
        }
      }
    }
    return fallback;
  };
  const assetManagerErrors = useMemo(() => {
    const errors = {};
    if (!String(assetManagerForm.role || "").trim()) errors.role = "Role is required.";
    if (!String(assetManagerForm.full_name || "").trim()) errors.full_name = "Full name is required.";
    if (!String(assetManagerForm.email || "").trim()) errors.email = "Email is required.";
    if (!String(assetManagerForm.country_code || "").trim()) errors.country_code = "Country code is required.";
    if (!String(assetManagerForm.phone_number || "").trim()) errors.phone_number = "Mobile number is required.";
    if (!String(assetManagerForm.nationality || "").trim()) errors.nationality = "Nationality is required.";
    const password = String(assetManagerForm.password || "");
    if (password.trim().length < 8) {
      errors.password = "Password must be at least 8 characters.";
    } else if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      errors.password = "Password must include at least one letter and one number.";
    }
    return errors;
  }, [assetManagerForm]);
  const assetManagerFormValid = Object.keys(assetManagerErrors).length === 0;

  const inferProjectOverviewPdfContentType = (file) => {
    const explicit = String(file?.type || "").toLowerCase().trim();
    if (explicit === "application/pdf") return "application/pdf";
    const name = String(file?.name || "").toLowerCase().trim();
    if (name.endsWith(".pdf")) return "application/pdf";
    return "";
  };

  const uploadProjectOverviewPdf = async (file, onUploaded) => {
    if (!file) return;
    const contentType = inferProjectOverviewPdfContentType(file);
    if (!contentType) {
      setMessage("Project overview file must be a PDF.");
      return;
    }
    setProjectOverviewUploadBusy(true);
    try {
      const { data } = await presignAdminProjectOverviewPdfUpload({
        file_name: file.name,
        content_type: contentType,
      });
      if (data.max_bytes && file.size > data.max_bytes) {
        setMessage(`Project PDF too large (max ${Math.round(data.max_bytes / 1024 / 1024)} MB).`);
        return;
      }
      const putRes = await fetch(data.upload_url, {
        method: "PUT",
        headers: { "Content-Type": data.content_type || contentType },
        body: file,
      });
      if (!putRes.ok) {
        // Fallback: direct backend upload when browser-to-S3 upload fails (often due CORS/presign constraints).
        const formData = new FormData();
        formData.append("file", file);
        const directRes = await uploadAdminProjectOverviewPdf(formData);
        const key = directRes?.data?.key || null;
        if (!key) {
          setMessage(`Project PDF upload failed (${putRes.status}).`);
          return null;
        }
        if (typeof onUploaded === "function") {
          await onUploaded(key);
        } else {
          setMessage("Project overview PDF uploaded.");
        }
        return key;
      }
      if (typeof onUploaded === "function") {
        await onUploaded(data.key);
      } else {
        setMessage("Project overview PDF uploaded.");
      }
      return data.key;
    } catch (err) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const directRes = await uploadAdminProjectOverviewPdf(formData);
        const key = directRes?.data?.key || null;
        if (!key) {
          setMessage(getErrorMessage(err, "Could not upload project overview PDF."));
          return null;
        }
        if (typeof onUploaded === "function") {
          await onUploaded(key);
        } else {
          setMessage("Project overview PDF uploaded.");
        }
        return key;
      } catch (fallbackErr) {
        setMessage(getErrorMessage(fallbackErr, getErrorMessage(err, "Could not upload project overview PDF.")));
        return null;
      }
    } finally {
      setProjectOverviewUploadBusy(false);
    }
  };

  const openProjectOverviewPdf = async (project) => {
    if (!project?.id) return;
    try {
      const { data } = await getProjectOverviewPdfSignedUrl(project.id);
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        setMessage("No signed URL returned for project overview PDF.");
      }
    } catch (err) {
      setMessage(getErrorMessage(err, "Could not open project overview PDF."));
    }
  };

  const openProjectOverviewPdfByKey = async (key) => {
    const ref = String(key || "").trim();
    if (!ref) return;
    try {
      const { data } = await getAdminProjectOverviewPdfSignedUrlByKey({ key: ref });
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        setMessage("No signed URL returned for project overview PDF.");
      }
    } catch (err) {
      setMessage(getErrorMessage(err, "Could not open project overview PDF."));
    }
  };

  const removeProjectOverviewPdf = async ({ key, projectId, onRemoved }) => {
    const ref = String(key || "").trim();
    if (!ref) return;
    setProjectOverviewDeleteBusy(true);
    try {
      await deleteAdminProjectOverviewPdf({
        key: ref,
        ...(projectId ? { project_id: projectId } : {}),
      });
      if (typeof onRemoved === "function") onRemoved();
      setMessage("Project overview PDF deleted. You can now upload a replacement.");
      if (projectId) {
        await load();
      }
    } catch (err) {
      setMessage(getErrorMessage(err, "Could not delete project overview PDF."));
    } finally {
      setProjectOverviewDeleteBusy(false);
    }
  };

  const confirmAndRemoveProjectOverviewPdf = async (params) => {
    const ok = window.confirm("Delete the current project overview PDF? This cannot be undone.");
    if (!ok) return;
    await removeProjectOverviewPdf(params);
  };

  const persistProjectOverviewPdfOnEdit = async (projectId, key) => {
    const pid = String(projectId || "").trim();
    const ref = String(key || "").trim();
    if (!pid || !ref) return;
    setProjectOverviewAttachBusy(true);
    try {
      await updateAdminProject(pid, { overview_pdf_url: ref });
      await load();
      setMessage("Project overview PDF uploaded and attached to the project.");
    } catch (err) {
      setMessage(getErrorMessage(err, "PDF uploaded but could not be attached to project. Please click Save Changes."));
    } finally {
      setProjectOverviewAttachBusy(false);
    }
  };

  const closeDepositDocPreview = () => {
    setDepositDocPreview({
      open: false,
      loading: false,
      url: "",
      error: "",
      title: "Deposit proof",
      subtitle: "",
      authRequired: false,
      contentHint: "",
    });
  };

  const openDepositProof = async (d) => {
    const depositId = d?.id;
    if (!depositId) return;
    setMessage("");
    setDepositDocPreview({
      open: true,
      loading: true,
      url: "",
      error: "",
      title: "Deposit proof",
      subtitle: [d.user_name || d.user_email, d.bank_channel, d.transaction_ref, d.amount != null ? `BDT ${d.amount}` : ""]
        .filter(Boolean)
        .join(" · "),
      authRequired: false,
      contentHint: "",
    });
    try {
      const { data } = await getAdminDepositScreenshotSignedUrl(depositId);
      const url = data?.url || "";
      if (!url) {
        setDepositDocPreview((prev) => ({
          ...prev,
          loading: false,
          error: "No document URL returned for this proof.",
        }));
        setMessage("No signed URL returned for this proof.");
        return;
      }
      setDepositDocPreview((prev) => ({
        ...prev,
        loading: false,
        url,
        error: "",
        authRequired: Boolean(data?.auth_required),
        contentHint: data?.content_hint || "",
      }));
    } catch (err) {
      const msg = getErrorMessage(err, "Could not open proof.");
      setDepositDocPreview((prev) => ({ ...prev, loading: false, url: "", error: msg }));
      setMessage(msg);
    }
  };

  const inferProofContentType = (file) => {
    const explicit = (file?.type || "").toLowerCase().trim();
    if (["image/jpeg", "image/png", "application/pdf"].includes(explicit)) return explicit;
    const name = (file?.name || "").toLowerCase();
    if (name.endsWith(".jpg") || name.endsWith(".jpeg")) return "image/jpeg";
    if (name.endsWith(".png")) return "image/png";
    if (name.endsWith(".pdf")) return "application/pdf";
    return "";
  };

  const uploadWithdrawalProof = async (withdrawalId, file) => {
    if (!file) return;
    const ct = inferProofContentType(file);
    if (!ct) {
      setMessage("Withdrawal proof must be JPG/JPEG, PNG, or PDF.");
      return;
    }
    const key = String(withdrawalId);
    setWithdrawalProofBusy((prev) => ({ ...prev, [key]: true }));
    setMessage("");
    try {
      const { data } = await presignAdminWithdrawalProofUpload({ file_name: file.name, content_type: ct });
      if (data.max_bytes && file.size > data.max_bytes) {
        setMessage(`File too large (max ${Math.round(data.max_bytes / 1024 / 1024)} MB).`);
        return;
      }
      const putRes = await fetch(data.upload_url, {
        method: "PUT",
        headers: { "Content-Type": data.content_type || ct },
        body: file,
      });
      if (!putRes.ok) {
        setMessage(`Proof upload failed (${putRes.status}).`);
        return;
      }
      setWithdrawalProofRefs((prev) => ({ ...prev, [key]: data.key }));
      setMessage("Withdrawal proof uploaded.");
    } catch (err) {
      setMessage(getErrorMessage(err, "Could not upload withdrawal proof."));
    } finally {
      setWithdrawalProofBusy((prev) => ({ ...prev, [key]: false }));
    }
  };

  const openWithdrawalProof = async (w) => {
    const ref = String(w.payout_proof_url || withdrawalProofRefs[String(w.id)] || "").trim();
    if (!ref) return;
    setMessage("");
    if (/^https?:\/\//i.test(ref)) {
      window.open(ref, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const { data } = await getAdminWithdrawalProofSignedUrl(w.id);
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        setMessage("No signed URL returned for withdrawal proof.");
      }
    } catch (err) {
      setMessage(getErrorMessage(err, "Could not open withdrawal proof."));
    }
  };

  const load = async () => {
    const canDashboard = hasAdminModuleAccess(user, "DASHBOARD");
    const canControl = hasAdminModuleAccess(user, "CONTROL");
    const canOperations = hasAdminModuleAccess(user, "OPERATIONS");
    const canPayments = hasAdminModuleAccess(user, "PAYMENTS");

    const tasks = [];
    if (canDashboard) tasks.push(fetchAdminAnalytics().then((r) => ["analytics", r.data]));
    if (canControl) {
      tasks.push(fetchPlatformSettings().then((r) => ["settings", r.data]));
      tasks.push(listAdminUsers(userFilter).then((r) => ["users", r.data]));
      tasks.push(listAdminKyc().then((r) => ["kyc", r.data]));
    }
    if (canOperations) {
      tasks.push(listAdminProjects().then((r) => ["projects", r.data]));
      tasks.push(listAdminInvestments().then((r) => ["investments", r.data]));
      tasks.push(listAdminPnl().then((r) => ["pnl", r.data]));
    }
    if (canPayments) {
      tasks.push(listAdminDeposits().then((r) => ["deposits", r.data]));
      tasks.push(listAdminWithdrawals().then((r) => ["withdrawals", r.data]));
      tasks.push(listAdminTransactions(txnFilter).then((r) => ["transactions", r.data]));
    }

    const settled = await Promise.allSettled(tasks);
    const dataByKey = {};
    let hadForbidden = false;
    for (const result of settled) {
      if (result.status === "fulfilled") {
        const [key, data] = result.value;
        dataByKey[key] = data;
      } else if (result.reason?.response?.status === 403) {
        hadForbidden = true;
      }
    }

    setAnalytics(dataByKey.analytics ?? null);
    setSettings(dataByKey.settings ?? null);
    setUsers(dataByKey.users ?? []);
    const projectsData = dataByKey.projects ?? [];
    let normalizedProjects = projectsData;
    if (canOperations && projectsData.length > 0) {
      const promotable = projectsData.filter((p) => {
        const status = String(p?.status || "").toUpperCase();
        const total = Number(p?.total_shares || 0);
        const sold = Number(p?.shares_sold || 0);
        return status === "FUNDING" && total > 0 && sold >= total;
      });
      if (promotable.length > 0) {
        await Promise.allSettled(
          promotable.map((p) => updateAdminProject(p.id, { status: "LOCK_IN" }))
        );
        try {
          const refreshedProjects = await listAdminProjects();
          normalizedProjects = refreshedProjects.data;
        } catch {
          normalizedProjects = projectsData;
        }
      }
    }
    setProjects(normalizedProjects);
    setInvestments(dataByKey.investments ?? []);
    setPnlItems(dataByKey.pnl ?? []);
    setDeposits(unwrapAdminList(dataByKey.deposits));
    setWithdrawals(unwrapAdminList(dataByKey.withdrawals));
    setKycItems(unwrapAdminList(dataByKey.kyc));
    setTransactions(unwrapAdminList(dataByKey.transactions));
    if (hadForbidden) {
      setMessage("Some data could not be loaded for your role permissions.");
    }
    if (user?.role === "SUPER_ADMIN") {
      try {
        const res = await fetchSuperAdminDepositOverview();
        setSuperDepositOverview(res.data);
      } catch {
        setSuperDepositOverview(null);
      }
    } else {
      setSuperDepositOverview(null);
    }
  };

  const runAdminAction = async (action, successMessage, fallbackMessage) => {
    try {
      await action();
      if (successMessage) {
        setMessage(successMessage);
      }
      await load();
    } catch (err) {
      setMessage(getErrorMessage(err, fallbackMessage));
    }
  };

  useEffect(() => {
    load().catch(() => setMessage("Failed to load admin data."));
    // Initial fetch for admin hub modules.
  }, []);

  useEffect(() => {
    if (location.pathname !== "/admin/control") return;
    const params = new URLSearchParams(location.search);
    const view = (params.get("view") || "").trim().toLowerCase();
    const tab = (params.get("tab") || "").trim().toUpperCase();
    if (view === "users" || tab === "USERS") {
      navigate("/admin/users", { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (location.pathname !== "/admin/users") return;
    const p = new URLSearchParams(location.search);
    setUserFilter((prev) => {
      const next = { ...prev };
      if (p.has("status")) next.status = p.get("status") || "";
      if (p.has("kyc_status")) next.kyc_status = p.get("kyc_status") || "";
      if (p.has("q")) next.q = p.get("q") || "";
      return next;
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (usersOnlyMode) {
      setActiveTab("USERS");
      return;
    }
    const raw = (new URLSearchParams(location.search).get("tab") || "").trim();
    if (!raw) return;
    const upper = raw.toUpperCase();
    if (upper === "USERS") {
      navigate("/admin/users", { replace: true });
      return;
    }
    if (upper !== "USERS" && TABS.includes(upper) && hasAdminModuleAccess(user, TAB_REQUIRED_MODULE[upper])) {
      setActiveTab(upper);
    }
  }, [location.pathname, location.search, usersOnlyMode, navigate, user]);

  useEffect(() => {
    if (usersOnlyMode) return;
    if (visibleTabs.length > 0 && !visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  }, [usersOnlyMode, visibleTabs, activeTab]);

  useEffect(() => {
    listAdminTransactions(txnFilter)
      .then((res) => setTransactions(res.data))
      .catch(() => {});
  }, [txnFilter]);

  useEffect(() => {
    if (!assetManagerToast) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" || e.key === "Enter") {
        e.preventDefault();
        setAssetManagerToast("");
        setAssetManagerToastLeadName("");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [assetManagerToast]);

  useEffect(() => {
    try {
      localStorage.setItem("adminControlTheme", adminTheme);
    } catch (_err) {}
  }, [adminTheme]);

  useEffect(() => {
    if (!assetManagerToast) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [assetManagerToast]);

  const onSave = async (e) => {
    e.preventDefault();
    await runAdminAction(
      () => updatePlatformSettings({
        referral_rate_pct: settings.referral_rate_pct,
        min_share_purchase: settings.min_share_purchase,
        max_share_ownership_pct: settings.max_share_ownership_pct,
        share_transfer_fee_pct: settings.share_transfer_fee_pct ?? "0.1",
        share_redemption_fee_pct: settings.share_redemption_fee_pct ?? "0.2",
        redemption_fee_base_pct: settings.redemption_fee_base_pct ?? settings.share_redemption_fee_pct ?? "0.2",
        max_redemption_pct_of_holdings: settings.max_redemption_pct_of_holdings ?? "1.0",
        min_wallet_deposit_bdt: settings.min_wallet_deposit_bdt ?? "100",
        min_wallet_withdrawal_bdt: settings.min_wallet_withdrawal_bdt,
        wallet_withdrawal_fee_pct: settings.wallet_withdrawal_fee_pct ?? "0",
        wallet_withdrawal_fee_fixed_bdt: settings.wallet_withdrawal_fee_fixed_bdt ?? "0",
        deposit_approval_remark_required: Boolean(settings.deposit_approval_remark_required),
        default_lockin_months: settings.default_lockin_months ?? 6,
        pnl_admin_fee_pct: settings.pnl_admin_fee_pct ?? "0",
        ...(isSuperAdmin
          ? { asset_manager_commission_pct: settings.asset_manager_commission_pct ?? "0" }
          : {}),
        fundraising_secondary_statuses: settings.fundraising_secondary_statuses,
        secondary_market_statuses: settings.secondary_market_statuses,
        funded_overview_statuses: settings.funded_overview_statuses,
      }),
      "Settings updated.",
      "Failed to update settings."
    );
  };

  const onCreateAssetManager = async (e) => {
    e.preventDefault();
    setAssetManagerSubmitAttempted(true);
    setAssetManagerSubmitError("");
    if (!isSuperAdmin) {
      setAssetManagerSubmitError("Only Super Admin can create staff accounts.");
      return;
    }
    if (!assetManagerFormValid) {
      setAssetManagerSubmitError("Please fill all required Asset Manager fields correctly.");
      return;
    }
    setAssetManagerSubmitting(true);
    const createdName = String(assetManagerForm.full_name || "").trim() || "User";
    try {
      await createAdminStaffUser(assetManagerForm);
      setMessage("");
      setAssetManagerSubmitError("");
      setAssetManagerToastLeadName(createdName);
      const roleLabel = STAFF_ROLE_OPTIONS.find((r) => r.value === assetManagerForm.role)?.label || "Staff user";
      setAssetManagerToast(
        `has been added as ${roleLabel} successfully. You can now assign responsibilities for operations and management.`
      );
      setAssetManagerForm({
        role: "ASSET_MANAGER",
        full_name: "",
        email: "",
        country_code: "+880",
        phone_number: "",
        nationality: "Bangladeshi",
        password: "",
      });
      setAssetManagerSubmitAttempted(false);
      await load();
    } catch (err) {
      setAssetManagerSubmitError(getErrorMessage(err, "Failed to create Asset Manager account."));
    } finally {
      setAssetManagerSubmitting(false);
    }
  };

  const openPermissionsModal = (targetUser) => {
    setPermissionModal({
      open: true,
      userId: String(targetUser?.id || ""),
      userName: String(targetUser?.full_name || targetUser?.email || "User"),
      permissions: Array.isArray(targetUser?.admin_permissions) ? targetUser.admin_permissions : [],
    });
  };

  const togglePermission = (permKey) => {
    setPermissionModal((prev) => {
      const current = Array.isArray(prev.permissions) ? prev.permissions : [];
      const next = current.includes(permKey) ? current.filter((p) => p !== permKey) : [...current, permKey];
      return { ...prev, permissions: next };
    });
  };

  const saveUserPermissions = async () => {
    if (!permissionModal.userId) return;
    await runAdminAction(
      () => updateUserPermissions(permissionModal.userId, { admin_permissions: permissionModal.permissions }),
      "User permissions updated.",
      "Failed to update user permissions."
    );
    setPermissionModal({ open: false, userId: "", userName: "", permissions: [] });
  };

  const onCreateProject = async (e) => {
    e.preventDefault();
    setProjectCreateError("");
    if (projectCreateSubmitting) return;
    if (projectOverviewUploadBusy || projectOverviewDeleteBusy || projectOverviewAttachBusy) {
      setProjectCreateError("Please wait for project overview PDF processing to finish.");
      return;
    }
    const name = String(newProject.name || "").trim();
    const address = String(newProject.address || "").trim();
    const paragraphs = [newProject.description_p1, newProject.description_p2, newProject.description_p3];
    const description = joinDescriptionParagraphs(paragraphs);
    if (!name) {
      setProjectCreateError("Project name is required.");
      return;
    }
    if (!address) {
      setProjectCreateError("Project address is required.");
      return;
    }
    if (countFilledParagraphs(paragraphs) < 3) {
      setProjectCreateError("Project description needs at least three paragraphs.");
      return;
    }
    if (newProject.category === "OTHER" && !String(customProjectCategory || "").trim()) {
      setProjectCreateError('When category is "Others", enter a custom category name.');
      return;
    }
    const totalShares = Number(newProject.total_shares);
    if (!Number.isFinite(totalShares) || totalShares < 1) {
      setProjectCreateError("Total shares must be a number of at least 1.");
      return;
    }
    const initialPrice = Number(newProject.initial_share_price);
    if (!Number.isFinite(initialPrice) || initialPrice < 1) {
      setProjectCreateError("Initial share price must be a number of at least 1 (BDT).");
      return;
    }
    const expectedPct = Number(newProject.expected_return_pct);
    if (!Number.isFinite(expectedPct) || expectedPct < 0) {
      setProjectCreateError("Estimated return % must be a number of 0 or more.");
      return;
    }
    if (expectedPct > 999.99) {
      setProjectCreateError("Estimated return % cannot exceed 999.99 (backend limit).");
      return;
    }
    const fundraisingStart = String(newProject.fundraising_start || "").trim();
    const fundraisingEnd = String(newProject.fundraising_end || "").trim();
    if (!fundraisingStart || !fundraisingEnd) {
      setProjectCreateError("Share selling start and end dates are required.");
      return;
    }
    if (fundraisingEnd < fundraisingStart) {
      setProjectCreateError("Share selling end date must be on or after the start date.");
      return;
    }
    const riskDisclaimer = String(newProject.risk_disclaimer || "").trim();
    if (!riskDisclaimer) {
      setProjectCreateError("Risk disclosure is required.");
      return;
    }
    const videoUrl = String(newProject.video_url || "").trim();
    if (videoUrl) {
      try {
        // eslint-disable-next-line no-new
        new URL(videoUrl);
      } catch {
        setProjectCreateError("Project video must be a valid URL (or leave blank).");
        return;
      }
    }
    const maturity = new Date(`${fundraisingEnd}T00:00:00Z`);
    maturity.setUTCDate(maturity.getUTCDate() + 180);
    const maturityDate = maturity.toISOString().slice(0, 10);
    const overviewPdfRef = String(newProject.overview_pdf_url || createProjectOverviewKeyRef.current || "").trim();
    const descriptionWithCategory =
      newProject.category === "OTHER" && customProjectCategory.trim()
        ? `${description}\n\nCustom Category: ${customProjectCategory.trim()}`
        : description;
    const landingPublished = Boolean(newProject.landing_published);
    setProjectCreateSubmitting(true);
    try {
      const { data: created } = await createAdminProject({
        name,
        address,
        description: descriptionWithCategory,
        category: newProject.category,
        total_shares: totalShares,
        initial_share_price: String(initialPrice),
        expected_return_pct: String(expectedPct),
        fundraising_start: fundraisingStart,
        fundraising_end: fundraisingEnd,
        maturity_date: maturityDate,
        risk_disclaimer: riskDisclaimer,
        status: "DRAFT",
        overview_pdf_url: overviewPdfRef,
        video_url: videoUrl,
        landing_headline: name,
        landing_subline: address,
        landing_highlights: [],
        landing_terms_notes: "",
        landing_published: landingPublished,
      });
      const projectId = created?.id;
      if (projectId && overviewPdfRef && !String(created?.overview_pdf_url || "").trim()) {
        try {
          const { data: patched } = await updateAdminProject(projectId, { overview_pdf_url: overviewPdfRef });
          if (patched?.overview_pdf_url) created.overview_pdf_url = patched.overview_pdf_url;
        } catch {
          /* overview can be re-attached from edit */
        }
      }
      const heroFile = createProjectHeroFileRef.current;
      if (projectId && heroFile) {
        const fd = new FormData();
        fd.append("file", heroFile);
        try {
          await uploadAdminProjectHero(projectId, fd);
        } catch (heroErr) {
          setMessage(
            getErrorMessage(heroErr, "Project created, but the banner image failed to upload. Add it from Edit project.")
          );
        }
      }
      if (projectId && landingPublished) {
        try {
          const { data: pub } = await publishAdminProjectLanding(projectId, { published: true });
          if (pub?.landing_slug) created.landing_slug = pub.landing_slug;
        } catch {
          /* ignore — serializer may already have assigned slug */
        }
      }
      const shareHint =
        landingPublished && created?.landing_slug
          ? ` Share: ${window.location.origin}/p/${created.landing_slug}`
          : "";
      setMessage(`Project & landing created.${shareHint}`);
      setProjectCreateError("");
      setNewProject(createInitialNewProject());
      createProjectOverviewKeyRef.current = "";
      createProjectHeroFileRef.current = null;
      setCustomProjectCategory("");
      setProjectModalOpen(false);
      await load().catch(() => setMessage("Project created. Refresh the page if the list does not update."));
    } catch (err) {
      const net = typeof err?.message === "string" ? err.message : "";
      const isNetwork = !err?.response && (net === "Network Error" || err?.code === "ERR_NETWORK");
      setProjectCreateError(
        getErrorMessage(
          err,
          isNetwork
            ? "Cannot reach the API (network/CORS). Use REACT_APP_API_BASE_URL if the backend is not on this host, or add your page origin to Django CORS_ALLOWED_ORIGINS. With DEBUG=True, private LAN origins are allowed via regex. Super Admin also needs the Operations module to create projects."
            : "Failed to create project. Please sign in again if your session expired or you lack Operations access."
        )
      );
    } finally {
      setProjectCreateSubmitting(false);
    }
  };

  const handleAssignManager = async (projectId) => {
    const assetManagerId = managerAssignments[projectId];
    if (!assetManagerId) return;
    await runAdminAction(
      () => assignProjectManager(projectId, { asset_manager_id: assetManagerId }),
      "Asset manager assigned.",
      "Failed to assign asset manager."
    );
    setManagerAssignments((prev) => {
      const next = { ...prev };
      delete next[projectId];
      return next;
    });
  };

  const handleUnassignManager = async (projectId) => {
    await runAdminAction(
      () => unassignProjectManager(projectId),
      "Asset manager unassigned.",
      "Failed to unassign asset manager."
    );
  };

  const handleProjectExtend = async (projectId) => {
    await runAdminAction(
      () => extendAdminProject(projectId, { extend_days: 7 }),
      "Project timeline extended by 7 days.",
      "Failed to extend project timeline."
    );
  };

  const closeCreateProjectModal = () => {
    if (projectOverviewUploadBusy || projectOverviewDeleteBusy || projectOverviewAttachBusy) {
      const ok = window.confirm(
        "PDF upload or delete is still in progress. Close the form anyway? Your entered details will be cleared."
      );
      if (!ok) return;
    }
    setNewProject(createInitialNewProject());
    createProjectOverviewKeyRef.current = "";
    createProjectHeroFileRef.current = null;
    setCustomProjectCategory("");
    setProjectCreateError("");
    setProjectCreateSubmitting(false);
    setProjectModalOpen(false);
  };

  const handleDiscardDraftProject = (p) => {
    if (!window.confirm(`Discard draft "${projectTitleWithCode(p)}"? It will be marked as cancelled.`)) return;
    runAdminAction(
      () => updateAdminProject(p.id, { status: "CANCELLED" }),
      "Draft discarded.",
      "Failed to discard draft."
    );
  };

  const handlePauseProject = (p) => {
    runAdminAction(() => pauseAdminProject(p.id), "Project paused.", "Failed to pause project.");
  };

  const handleResumeProject = (p) => {
    runAdminAction(() => resumeAdminProject(p.id), "Project resumed.", "Failed to resume project.");
  };

  const handleCloseRedeemedProject = (p) => {
    if (
      !window.confirm(
        `Close "${projectTitleWithCode(p)}"? The project will be marked Closed — no further P&L or secondary market activity. SSA share holdings are unchanged.`
      )
    ) {
      return;
    }
    runAdminAction(
      () => closeRedeemedAdminProject(p.id),
      "Redeemed project closed.",
      "Failed to close project."
    );
  };

  const handleCancelListedProject = (p) => {
    const st = normalizeProjectStatus(p.status);
    if (st === "DRAFT") return;
    const sold = Number(p?.shares_sold || 0);
    const isEmergencyFundraisingCancel = ["FUNDING", "PAUSED"].includes(st) && sold > 0;
    if (isEmergencyFundraisingCancel) {
      const typed = window.prompt(
        `EMERGENCY CANCEL for "${projectTitleWithCode(p)}"\n\n` +
          `${sold} share(s) already sold. This will:\n` +
          `• Refund 100% of each investor's purchase price to their wallet\n` +
          `• Close the listing (cannot be undone)\n\n` +
          `Type CANCEL to confirm, then you will be asked for an audit reason.`
      );
      if (String(typed || "").trim().toUpperCase() !== "CANCEL") {
        return;
      }
      const reason = window.prompt("Audit reason for this emergency cancel (min 10 characters):");
      if (!reason || String(reason).trim().length < 10) {
        window.alert("Cancel aborted — a reason of at least 10 characters is required.");
        return;
      }
      runAdminAction(
        () =>
          cancelAdminProject(p.id, {
            confirm_full_refund: true,
            reason: String(reason).trim(),
          }),
        "Project cancelled; investors refunded at purchase price.",
        "Failed to cancel project."
      );
      return;
    }
    if (
      !window.confirm(
        `Cancel "${projectTitleWithCode(p)}"? This closes the listing. This cannot be undone.`
      )
    ) {
      return;
    }
    runAdminAction(
      () => cancelAdminProject(p.id, { reason: "Admin listing cancel (no shares sold)" }),
      "Project cancelled.",
      "Failed to cancel project."
    );
  };
  const openProjectEditModal = async (project) => {
    const existingOverviewRef = project.overview_pdf_url || "";
    editProjectOverviewKeyRef.current = existingOverviewRef;
    setProjectEditPendingPdfFile(null);
    setProjectEditPdfUploadProgress(null);
    const buildForm = (p) => {
      const paras = splitDescriptionParagraphs(p.description || "");
      return {
        name: p.name || "",
        address: p.address || "",
        description_p1: paras[0] || "",
        description_p2: paras[1] || "",
        description_p3: paras[2] || "",
        category: p.category || "CLINIC",
        total_shares: Number(p.total_shares || 0),
        initial_share_price: Number(p.initial_share_price || p.current_share_price || 0),
        expected_return_pct: Number(p.expected_return_pct || 0),
        fundraising_start: String(p.fundraising_start || "").slice(0, 10),
        fundraising_end: String(p.fundraising_end || "").slice(0, 10),
        risk_disclaimer: p.risk_disclaimer || "",
        overview_pdf_url: String(p.overview_pdf_url || "").trim(),
        video_url: p.video_url || "",
        landing_published: Boolean(p.landing_published),
        landing_slug: p.landing_slug || "",
        landing_hero_image_url: p.landing_hero_image_url || "",
      };
    };
    const stageKey = getStageKey(project);
    const control = adminProjectControlAction(stageKey);
    const pdfUpdateOnly = control.pdfUpdateOnly;
    // Structural share/price/dates lock when funded; about + landing + docs stay editable.
    // Offer terms (price + total shares) also lock after the first sale while still fundraising.
    const lifecycleLocked = isAdminProjectContentLocked(project, lifecycleBuckets);
    const offerTermsLocked = isAdminProjectOfferTermsLocked(project, lifecycleBuckets);
    const readOnly = false;
    setProjectEditModal({
      open: true,
      projectId: project.id,
      lifecycleLocked,
      offerTermsLocked,
      readOnly,
      pdfUpdateOnly,
      form: buildForm(project),
      supplyProject: project,
    });
    try {
      const { data } = await fetchProjectDetails(project.id);
      const latestOverviewRef = String(data?.overview_pdf_url || "").trim();
      editProjectOverviewKeyRef.current = latestOverviewRef;
      setProjectEditModal((prev) => {
        if (!prev.open || prev.projectId !== project.id) return prev;
        const refreshed = data || project;
        const stageKeyRefresh = getStageKey(refreshed);
        const controlRefresh = adminProjectControlAction(stageKeyRefresh);
        const pdfUpdateOnlyRefresh = controlRefresh.pdfUpdateOnly;
        const lifecycleLockedRefresh = isAdminProjectContentLocked(refreshed, lifecycleBuckets);
        const offerTermsLockedRefresh = isAdminProjectOfferTermsLocked(refreshed, lifecycleBuckets);
        return {
          ...prev,
          lifecycleLocked: lifecycleLockedRefresh,
          offerTermsLocked: offerTermsLockedRefresh,
          readOnly: false,
          pdfUpdateOnly: pdfUpdateOnlyRefresh,
          form: buildForm(refreshed),
          supplyProject: refreshed,
        };
      });
    } catch {
      // Keep modal usable even if detail refresh fails.
    }
  };

  const openLandingModal = async (project) => {
    const formFrom = (p) => ({
      landing_headline: p.landing_headline || "",
      landing_subline: p.landing_subline || "",
      landing_highlights_text: Array.isArray(p.landing_highlights) ? p.landing_highlights.join("\n") : "",
      landing_terms_notes: p.landing_terms_notes || "",
      landing_published: Boolean(p.landing_published),
      landing_slug: p.landing_slug || "",
      landing_hero_image_url: p.landing_hero_image_url || "",
    });
    setLandingModal({
      open: true,
      projectId: project.id,
      projectName: projectTitleWithCode(project),
      saving: false,
      form: formFrom(project),
    });
    try {
      const { data } = await fetchProjectDetails(project.id);
      setLandingModal((prev) => {
        if (!prev.open || prev.projectId !== project.id) return prev;
        return { ...prev, form: formFrom(data || project), projectName: projectTitleWithCode(data || project) };
      });
    } catch {
      /* keep initial */
    }
  };

  const saveLandingModal = async () => {
    const { projectId, form } = landingModal;
    if (!projectId || landingModal.saving) return;
    setLandingModal((prev) => ({ ...prev, saving: true }));
    const highlights = String(form.landing_highlights_text || "")
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 6);
    try {
      await updateAdminProject(projectId, {
        landing_headline: form.landing_headline,
        landing_subline: form.landing_subline,
        landing_highlights: highlights,
        landing_terms_notes: form.landing_terms_notes,
      });
      const { data: pub } = await publishAdminProjectLanding(projectId, {
        published: Boolean(form.landing_published),
      });
      const slug = pub?.landing_slug || form.landing_slug;
      setMessage(
        form.landing_published && slug
          ? `Landing published. Share: ${window.location.origin}/p/${slug}`
          : "Landing page saved."
      );
      setLandingModal((prev) => ({ ...prev, open: false, saving: false }));
      await load();
    } catch (err) {
      const d = err.response?.data;
      setMessage(
        (typeof d?.detail === "string" && d.detail) ||
          d?.landing_highlights?.[0] ||
          "Failed to save landing page."
      );
      setLandingModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const onLandingHeroFile = async (file, projectIdOverride) => {
    const pid = projectIdOverride || landingModal.projectId;
    if (!file || !pid) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await uploadAdminProjectHero(pid, fd);
      const heroKey = data.landing_hero_image_url || "";
      setLandingModal((prev) =>
        prev.projectId === pid
          ? { ...prev, form: { ...prev.form, landing_hero_image_url: heroKey } }
          : prev
      );
      setProjectEditModal((prev) =>
        prev.projectId === pid
          ? { ...prev, form: { ...prev.form, landing_hero_image_url: heroKey } }
          : prev
      );
      setMessage("Hero image uploaded.");
      await load();
    } catch (err) {
      setMessage(err.response?.data?.detail || "Hero upload failed.");
    }
  };

  const onLandingHeroDelete = async (projectIdOverride) => {
    const pid = projectIdOverride || landingModal.projectId;
    if (!pid) return;
    try {
      await deleteAdminProjectHero(pid);
      setLandingModal((prev) =>
        prev.projectId === pid ? { ...prev, form: { ...prev.form, landing_hero_image_url: "" } } : prev
      );
      setProjectEditModal((prev) =>
        prev.projectId === pid ? { ...prev, form: { ...prev.form, landing_hero_image_url: "" } } : prev
      );
      setMessage("Hero image removed.");
      await load();
    } catch (err) {
      setMessage(err.response?.data?.detail || "Could not remove hero image.");
    }
  };

  const saveProjectEdits = async () => {
    const { projectId } = projectEditModal;
    if (!projectId) return;
    if (projectOverviewUploadBusy || projectOverviewDeleteBusy || projectOverviewAttachBusy) {
      setMessage("Please wait for project overview PDF processing to finish.");
      return;
    }
    const workingForm = projectEditModal.form;
    const locked = projectEditModal.lifecycleLocked;
    const offerLocked = projectEditModal.offerTermsLocked;
    const paragraphs = [workingForm.description_p1, workingForm.description_p2, workingForm.description_p3];
    if (!String(workingForm.name || "").trim()) {
      setMessage("Project name is required.");
      return;
    }
    if (!String(workingForm.address || "").trim()) {
      setMessage("Project address is required.");
      return;
    }
    if (countFilledParagraphs(paragraphs) < 3) {
      setMessage("Project description needs at least three paragraphs.");
      return;
    }
    if (!String(workingForm.risk_disclaimer || "").trim()) {
      setMessage("Risk disclosure is required.");
      return;
    }
    const description = joinDescriptionParagraphs(paragraphs);
    const videoUrl = String(workingForm.video_url || "").trim();
    if (videoUrl) {
      try {
        // eslint-disable-next-line no-new
        new URL(videoUrl);
      } catch {
        setMessage("Project video must be a valid URL (or leave blank).");
        return;
      }
    }
    try {
      const basePayload = {
        name: workingForm.name,
        address: String(workingForm.address || "").trim(),
        description,
        category: workingForm.category,
        expected_return_pct: String(workingForm.expected_return_pct),
        risk_disclaimer: workingForm.risk_disclaimer,
        video_url: videoUrl,
        landing_headline: String(workingForm.name || "").trim(),
        landing_subline: String(workingForm.address || "").trim(),
      };
      let structuralPayload = {};
      if (!locked) {
        structuralPayload = {
          fundraising_start: workingForm.fundraising_start,
          fundraising_end: workingForm.fundraising_end,
        };
        if (!offerLocked) {
          structuralPayload = {
            ...structuralPayload,
            total_shares: Number(workingForm.total_shares),
            initial_share_price: String(workingForm.initial_share_price),
          };
        }
      }
      await updateAdminProject(projectId, { ...basePayload, ...structuralPayload });
      const { data: pub } = await publishAdminProjectLanding(projectId, {
        published: Boolean(workingForm.landing_published),
      });
      const slug = pub?.landing_slug || workingForm.landing_slug;
      await load();
      editProjectOverviewKeyRef.current = "";
      setProjectEditPendingPdfFile(null);
      setMessage(
        workingForm.landing_published && slug
          ? `Project updated. Share: ${window.location.origin}/p/${slug}`
          : "Project details and landing page saved."
      );
      setProjectEditModal((prev) => ({
        ...prev,
        open: false,
        projectId: "",
        lifecycleLocked: false,
        offerTermsLocked: false,
        readOnly: false,
        pdfUpdateOnly: false,
      }));
    } catch (err) {
      const serverError = err?.response?.data;
      if (serverError && typeof serverError === "object" && !serverError.detail) {
        setMessage(`Failed to update project details: ${JSON.stringify(serverError)}`);
      } else {
        setMessage(getErrorMessage(err, "Failed to update project details."));
      }
    }
  };
  const openProjectStageModal = (project) => {
    const currentStage = getStageKey(project);
    if (currentStage === "CANCELLED") {
      setProjectStageModal({
        open: true,
        projectId: project.id,
        currentStage,
        stage: "DRAFT",
        allowedStages: ["DRAFT"],
      });
      return;
    }
    const nextStageByCurrent = {
      DRAFT: "REVIEW",
      REVIEW: "FUNDRAISING",
    };
    const nextStage = nextStageByCurrent[currentStage] || null;
    if (!nextStage) {
      if (currentStage === "FUNDED") {
        setMessage("Funded projects cannot be advanced manually. Use redemption workflows and closure rules.");
      } else if (currentStage === "FUNDRAISING") {
        setMessage(
          "This project is already in fundraising. When all primary shares are sold, it moves to lock-in automatically (use Refresh on the dashboard if the list looks stale)."
        );
      } else if (currentStage === "PAUSED") {
        setMessage("Paused listings are resumed with “Resume listing” (Super Admin), not the stage picker.");
      } else {
        setMessage("Manual stage update is only available for Draft → Review, Review → Fundraising, and Cancelled → Draft.");
      }
      return;
    }
    setProjectStageModal({
      open: true,
      projectId: project.id,
      currentStage,
      stage: nextStage,
      allowedStages: [nextStage],
    });
  };
  const saveProjectStage = async () => {
    const { projectId, stage, allowedStages } = projectStageModal;
    if (!projectId) return;
    if (!allowedStages.includes(stage)) {
      setMessage("Invalid stage transition selected.");
      return;
    }
    const stageToStatus = {
      DRAFT: "DRAFT",
      REVIEW: "REVIEW",
      FUNDRAISING: "FUNDING",
    };
    const targetStatus = stageToStatus[stage] || "DRAFT";
    await runAdminAction(
      () => updateAdminProject(projectId, { status: targetStatus }),
      `Project stage updated to ${statusLabel(targetStatus)}.`,
      "Failed to update project stage."
    );
    setProjectStageModal({ open: false, projectId: "", currentStage: "DRAFT", stage: "DRAFT", allowedStages: [] });
  };


  const handleNoticeBroadcast = async (e) => {
    e.preventDefault();
    await runAdminAction(
      () => broadcastNotice(noticeForm),
      "System notice broadcasted.",
      "Failed to broadcast notice."
    );
    setNoticeForm({ ...noticeForm, title: "", message: "" });
  };

  const openRejectModal = (kind, id, title, depositIds = [], investmentTxType = "") => {
    setRejectModal({
      open: true,
      kind,
      id: id || "",
      depositIds: Array.isArray(depositIds) ? depositIds : [],
      investmentTxType: typeof investmentTxType === "string" ? investmentTxType : "",
      reason: "",
      title,
    });
  };

  const openDepositApproveModal = (kind, id, depositIds = [], amountLabel = "") => {
    setDepositApproveModal({
      open: true,
      kind,
      id: id || "",
      depositIds: Array.isArray(depositIds) ? depositIds : [],
      amountLabel: amountLabel || "",
      remark: "",
      submitting: false,
    });
  };

  const submitDepositApprove = async () => {
    const remark = depositApproveModal.remark.trim();
    if (settings?.deposit_approval_remark_required && !remark) {
      setMessage("Admin remark is required when approving deposits (platform setting).");
      return;
    }
    if (remark.length > 1000) {
      setMessage("Admin remark cannot exceed 1000 characters.");
      return;
    }
    setDepositApproveModal((m) => ({ ...m, submitting: true }));
    setMessage("");
    try {
      if (depositApproveModal.kind === "single") {
        await reviewAdminDeposit(depositApproveModal.id, { status: "APPROVED", admin_remark: remark });
        setMessage("Deposit approved.");
      } else if (depositApproveModal.kind === "bulk") {
        await bulkReviewAdminDeposits({
          deposit_ids: depositApproveModal.depositIds,
          status: "APPROVED",
          admin_remark: remark,
        });
        setMessage(`Approved ${depositApproveModal.depositIds.length} deposit(s).`);
      }
      setDepositApproveModal({
        open: false,
        kind: "",
        id: "",
        depositIds: [],
        amountLabel: "",
        remark: "",
        submitting: false,
      });
      setSelectedDepositIds([]);
      await load();
    } catch (err) {
      setMessage(getErrorMessage(err, "Failed to approve deposit(s)."));
      setDepositApproveModal((m) => ({ ...m, submitting: false }));
    }
  };

  const submitReject = async () => {
    const reason = rejectModal.reason.trim();
    if (!reason) {
      setMessage("Rejection reason is required.");
      return;
    }
    let action = null;
    if (rejectModal.kind === "deposit_reject") {
      action = () => reviewAdminDeposit(rejectModal.id, { status: "REJECTED", rejection_reason: reason });
    } else if (rejectModal.kind === "deposit_bulk_reject") {
      action = () =>
        bulkReviewAdminDeposits({
          deposit_ids: rejectModal.depositIds,
          status: "REJECTED",
          rejection_reason: reason,
        });
    } else if (rejectModal.kind === "withdraw_reject") {
      action = () => reviewAdminWithdrawal(rejectModal.id, { status: "REJECTED", failure_reason: reason });
    } else if (rejectModal.kind === "withdraw_fail") {
      action = () => reviewAdminWithdrawal(rejectModal.id, { status: "FAILED", failure_reason: reason });
    } else if (rejectModal.kind === "kyc_reject") {
      action = () => reviewAdminKyc(rejectModal.id, { status: "REJECTED", rejection_reason: reason });
    } else if (rejectModal.kind === "investment_reject") {
      const isRedemption = String(rejectModal.investmentTxType || "").toUpperCase() === "REDEMPTION";
      action = () =>
        (isRedemption ? reviewAdminRedemption : reviewAdminInvestment)(rejectModal.id, { status: "REJECTED" });
    } else if (rejectModal.kind === "pnl_reject") {
      action = () => reviewAdminPnl(rejectModal.id, { status: "REJECTED", rejection_reason: reason });
    }
    if (!action) return;
    await runAdminAction(action, "Action completed.", "Failed to submit rejection.");
    setRejectModal({ open: false, kind: "", id: "", depositIds: [], investmentTxType: "", reason: "", title: "" });
    setSelectedDepositIds([]);
  };

  const lifecycleBuckets = useMemo(() => buildLifecycleBuckets(projects, settings), [projects, settings]);
  const fundraisingIds = new Set(lifecycleBuckets.fundraising.map((p) => p.id));
  const fundedIds = new Set(lifecycleBuckets.funded.map((p) => p.id));
  const completedIds = new Set(lifecycleBuckets.completed.map((p) => p.id));
  const getStageKey = (project) => {
    const status = normalizeProjectStatus(project?.status);
    if (status === "DRAFT") return "DRAFT";
    if (status === "REVIEW" || status === "OPEN") return "REVIEW";
    if (status === "CANCELLED") return "CANCELLED";
    if (completedIds.has(project?.id) || isCompletedProjectStatus(status)) return "TERMINAL";
    if (fundedIds.has(project?.id)) return "FUNDED";
    if (fundraisingIds.has(project?.id)) return "FUNDRAISING";
    return "OTHER";
  };
  const statusLabel = (statusRaw) => {
    const status = String(statusRaw || "").toUpperCase();
    if (["FUNDING", "PAUSED"].includes(status)) return "FUNDRAISING";
    if (status === "LOCK_IN") return "FUNDED";
    if (status === "CANCELLED") return "Cancelled";
    if (status === "REVIEW" || status === "OPEN") return "Review";
    if (status === "REDEEMED" || status === "COMPLETED") return "Redeemed";
    if (status === "MATURED") return "Matured";
    if (status === "CLOSED") return "Closed";
    return status || "-";
  };
  const formatProjectCode = (codeRaw) => {
    const raw = String(codeRaw || "").trim().toUpperCase();
    const m = raw.match(/^([A-Z]+)[-\s]?(\d+)$/);
    if (!m) return raw;
    return `${m[1]}${m[2].padStart(2, "0")}`;
  };
  const projectTitleWithCode = (project) => {
    const name = String(project?.name || "").trim();
    const code = formatProjectCode(project?.project_code);
    if (code && name) return `${code}: ${name}`;
    if (name) return name;
    return code || "-";
  };
  const projectTitleById = (projectId) => {
    const match = projects.find((p) => String(p.id) === String(projectId));
    if (!match) return String(projectId || "-");
    return projectTitleWithCode(match);
  };
  const money = (value) => Number(value || 0).toFixed(2);
  /**
   * Gross BDT for admin row. Pending redemptions must not trust stale ``total_bdt`` when ``live_nav_per_share``
   * is present (older rows stored wrong gross at listing NAV).
   */
  const pendingInvestmentGrossBdt = (row) => {
    const sh = Number(row?.shares);
    const nav = Number(row?.price_per_share);
    const pen = String(row?.status || "").toUpperCase() === "PENDING";
    const red = String(row?.type || "").toUpperCase() === "REDEMPTION";
    if (pen && red) {
      const rawLive = row?.live_nav_per_share ?? row?.liveNavPerShare;
      if (rawLive != null && rawLive !== "") {
        const live = Number(rawLive);
        if (Number.isFinite(live) && Number.isFinite(sh)) return sh * live;
      }
      if (Number.isFinite(sh) && Number.isFinite(nav)) return sh * nav;
      const gross = Number(row?.total_bdt);
      return Number.isFinite(gross) ? gross : 0;
    }
    const gross = Number(row?.total_bdt);
    if (Number.isFinite(gross) && gross > 0) return gross;
    if (Number.isFinite(sh) && Number.isFinite(nav)) return sh * nav;
    return 0;
  };
  /** Pending redemption: API ``live_nav_per_share`` = current investor NAV (same as project card). */
  const pendingRedemptionDisplayNav = (row) => {
    if (String(row?.status || "").toUpperCase() !== "PENDING") return null;
    if (String(row?.type || "").toUpperCase() !== "REDEMPTION") return null;
    const raw = row?.live_nav_per_share ?? row?.liveNavPerShare;
    if (raw == null || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };
  const adminInvestmentNavBdt = (row) => {
    const live = pendingRedemptionDisplayNav(row);
    if (live != null) return live;
    return Number(row?.price_per_share || 0);
  };
  const canShowExtendAction = (project) => {
    const status = String(project?.status || "").toUpperCase();
    if (!["FUNDING", "PAUSED"].includes(status)) return false;
    const endRaw = String(project?.fundraising_end || "").trim();
    if (!endRaw) return false;
    const endDate = new Date(`${endRaw}T00:00:00`);
    if (Number.isNaN(endDate.getTime())) return false;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const msPerDay = 1000 * 60 * 60 * 24;
    const daysUntilEnd = Math.ceil((endDate.getTime() - today.getTime()) / msPerDay);
    return daysUntilEnd >= 0 && daysUntilEnd <= 10;
  };
  const managedUsers = users.filter((u) => ["ASSET_MANAGER", "ADMIN", "ACCOUNTANT", "MARKETING"].includes(String(u.role || "").toUpperCase()));
  const assetManagers = managedUsers.filter((u) => String(u.role || "").toUpperCase() === "ASSET_MANAGER");
  const pendingKyc = kycItems.filter((k) => k.status === "PENDING");
  const pendingInvestments = investments.filter((i) => i.status === "PENDING");
  const pendingRedemptionRows = pendingInvestments.filter((i) => String(i.type || "").toUpperCase() === "REDEMPTION");
  const pendingOtherInvestmentRows = pendingInvestments.filter((i) => String(i.type || "").toUpperCase() !== "REDEMPTION");
  const pastRedemptionRows = [...investments]
    .filter(
      (i) =>
        String(i.type || "").toUpperCase() === "REDEMPTION" && String(i.status || "").toUpperCase() !== "PENDING"
    )
    .sort((a, b) => {
      const ta = new Date(a.requested_at || 0).getTime();
      const tb = new Date(b.requested_at || 0).getTime();
      return tb - ta;
    });
  const pnlRecordStatusLabel = (row) => {
    const key = String(row?.display_status || row?.status || "").toUpperCase();
    if (key === "UPDATED") return "Updated";
    if (key === "PENDING") return "Pending";
    if (key === "APPROVED") return "Approved";
    if (key === "REJECTED") return "Rejected";
    if (key === "CANCELLED") return "Cancelled";
    return key || "-";
  };
  const pnlRecordStatusBadge = (row) => String(row?.display_status || row?.status || "").toUpperCase();
  const pendingPnl = pnlItems.filter((p) => p.status === "PENDING");
  const pnlHistoryRows = useMemo(() => {
    let rows = [...pnlItems].sort((a, b) => new Date(b.submitted_at || 0) - new Date(a.submitted_at || 0));
    if (pnlHistoryProjectFilter) {
      rows = rows.filter((p) => String(p.project) === String(pnlHistoryProjectFilter));
    }
    return rows;
  }, [pnlItems, pnlHistoryProjectFilter]);
  const pnlProjectOptions = useMemo(() => {
    const ids = new Set(pnlItems.map((p) => String(p.project)));
    return projects.filter((p) => ids.has(String(p.id)));
  }, [pnlItems, projects]);
  const pnlTotalsByProject = usePnlTotalsByProject(pnlItems);
  const pendingRedemptionCountOverview = useMemo(() => {
    const raw = analytics?.pending_redemptions_count;
    if (raw != null && raw !== "") {
      const n = Number(raw);
      if (Number.isFinite(n)) return n;
    }
    return pendingRedemptionRows.length;
  }, [analytics?.pending_redemptions_count, pendingRedemptionRows.length]);
  const draftOpenRows = useMemo(
    () =>
      sortProjectsNewestFirst(
        projects.filter((p) => ["DRAFT", "REVIEW"].includes(normalizeProjectStatus(p.status)))
      ),
    [projects]
  );
  const marketRows = useMemo(
    () =>
      sortProjectsNewestFirst(
        [...lifecycleBuckets.fundraising, ...lifecycleBuckets.funded].filter((p) => {
          const status = normalizeProjectStatus(p?.status);
          return !["DRAFT", "REVIEW"].includes(status);
        })
      ),
    [lifecycleBuckets.fundraising, lifecycleBuckets.funded]
  );
  const marketRowsFiltered = useMemo(
    () => marketRows.filter((p) => projectFilter === "ALL" || getStageKey(p) === projectFilter),
    [marketRows, projectFilter]
  );
  const redeemedAndClosedRows = useMemo(
    () => sortProjectsNewestFirst(projects.filter((p) => isRedeemedOrClosedProjectStatus(p?.status))),
    [projects]
  );
  const cancelledRows = useMemo(
    () => sortProjectsNewestFirst(lifecycleBuckets.cancelled || []),
    [lifecycleBuckets.cancelled]
  );
  const managerAssignmentEligible = useMemo(
    () => buildManagerAssignmentRows(lifecycleBuckets),
    [lifecycleBuckets]
  );
  const unassignedManagerRows = useMemo(
    () => managerAssignmentEligible.filter((p) => !p.active_asset_manager),
    [managerAssignmentEligible]
  );
  const assignedManagerRows = useMemo(
    () => managerAssignmentEligible.filter((p) => p.active_asset_manager),
    [managerAssignmentEligible]
  );
  const assetManagerNameById = useMemo(() => {
    const map = {};
    assetManagers.forEach((m) => {
      map[m.id] = m.full_name || m.email || m.id;
    });
    return map;
  }, [assetManagers]);
  const pendingDepositsOnPage = paginate(deposits, pages.deposits, 10).filter((d) => d.status === "PENDING");
  const effectiveModuleKeys = useMemo(() => getAllowedAdminModules(user), [user]);
  const effectiveModuleLabels = ADMIN_PERMISSION_OPTIONS
    .filter((opt) => effectiveModuleKeys.includes(opt.key))
    .map((opt) => opt.label);

  const toggleDepositSelect = (id) => {
    const s = String(id);
    setSelectedDepositIds((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };
  const closeAssetManagerPopup = () => {
    setAssetManagerToast("");
    setAssetManagerToastLeadName("");
  };

  if (!["ADMIN", "SUPER_ADMIN", "ACCOUNTANT", "MARKETING"].includes(user?.role)) {
    return (
      <div className="page">
        <h2>Admin Control</h2>
        <p className="msg">Access denied.</p>
      </div>
    );
  }

  return (
    <div className={`page admin-friendly-page ${adminTheme === "contrast" ? "admin-friendly-page--contrast" : ""}`}>
      <SuccessPopup
        open={Boolean(assetManagerToast)}
        leadName={assetManagerToastLeadName}
        message={assetManagerToast}
        onClose={closeAssetManagerPopup}
      />
      <h2>{usersOnlyMode ? "User Management" : "Admin Control"}</h2>
      <div className={`admin-friendly-hero admin-friendly-hero--${TAB_META[activeTab]?.tone || "ops"}`}>
        <div>
          <strong>Super Admin Workspace</strong>
          <p style={{ margin: "6px 0 0" }}>
            {TAB_META[activeTab]?.hint || "Manage platform operations and governance from one place."}
          </p>
        </div>
        <div className="admin-hero-side">
          <span className="admin-friendly-role-pill">
            Role: {isSuperAdmin ? "Super Admin" : "Staff Admin"}
          </span>
          <div className="admin-module-strip">
            {effectiveModuleLabels.length > 0 ? (
              effectiveModuleLabels.map((label) => (
                <span key={label} className="admin-module-pill">{label}</span>
              ))
            ) : (
              <span className="admin-module-pill">No module access</span>
            )}
          </div>
          <button type="button" className="btn-secondary admin-hero-logout" onClick={logout}>
            Log out
          </button>
        </div>
      </div>
      {message && (
        <p className={`msg ${messageIsError ? "msg-error" : "msg-success"}`} role={messageIsError ? "alert" : "status"}>
          {message}
        </p>
      )}
      <div className="card admin-tabs-card" style={{ padding: 10, marginBottom: 12 }}>
        <div className="admin-tab-wrap">
          {visibleTabs.map((tab) => (
            <button
              key={tab}
              className={`admin-tab-chip ${activeTab === tab ? "admin-tab-chip--active" : "btn-secondary"}`}
              onClick={() => setActiveTab(tab)}
            >
              <span className="admin-tab-icon" aria-hidden="true">{TAB_ICONS[tab] || "•"}</span>
              <span>{TAB_LABELS[tab]}</span>
            </button>
          ))}
          <button
            className="btn-secondary"
            type="button"
            onClick={() => setAdminTheme((prev) => (prev === "contrast" ? "soft" : "contrast"))}
            aria-pressed={adminTheme === "contrast"}
          >
            {adminTheme === "contrast" ? "Switch to Soft Theme" : "Switch to High Contrast"}
          </button>
          <button
            className="btn-secondary"
            style={{ marginLeft: "auto" }}
            onClick={() => runAdminAction(() => Promise.resolve(), "Dashboard refreshed.", "Failed to refresh dashboard.")}
          >
            Refresh
          </button>
        </div>
      </div>
      {!usersOnlyMode && <div className="admin-quick-guide">
        <div className="admin-quick-guide__item admin-quick-guide__item--ops">
          <strong>Operations</strong>
          <p>Overview tab for shortcuts; Projects and KYC for daily workflow monitoring.</p>
        </div>
        <div className="admin-quick-guide__item admin-quick-guide__item--finance">
          <strong>Finance</strong>
          <p>Wallet &amp; Transactions plus Profit Management for monetary controls.</p>
        </div>
        <div className="admin-quick-guide__item admin-quick-guide__item--staff">
          <strong>People</strong>
          <p>New investor signups: use <strong>Users (new signups)</strong> in the sidebar. Asset Managers for staff accounts and permissions.</p>
        </div>
      </div>}

      {activeTab === "OVERVIEW" && (
      <div className="card" style={{ padding: 14 }}>
        <h3>Operational Snapshot</h3>
        {superDepositOverview && (
          <div className="card" style={{ padding: 12, marginBottom: 12, background: "var(--surface-alt)" }}>
            <strong>Super Admin Deposit Visibility</strong>
            <div className="kpis" style={{ marginTop: 8 }}>
              <div className="kpi"><div className="kpi-label">Total Deposited</div><div className="kpi-value">{superDepositOverview.total_deposited_amount}</div></div>
              <div className="kpi"><div className="kpi-label">Pending</div><div className="kpi-value">{superDepositOverview.totals_by_status?.pending?.count ?? 0}</div></div>
              <div className="kpi"><div className="kpi-label">Approved</div><div className="kpi-value">{superDepositOverview.totals_by_status?.approved?.count ?? 0}</div></div>
              <div className="kpi"><div className="kpi-label">Cancelled</div><div className="kpi-value">{superDepositOverview.totals_by_status?.cancelled?.count ?? 0}</div></div>
            </div>
            <p className="muted" style={{ marginBottom: 0 }}>
              Includes cancellation reasons and full deposit audit trail through the super-overview API.
            </p>
          </div>
        )}
        <div className="card stack" style={{ padding: 12, marginBottom: 14, background: "var(--surface-alt)" }}>
          <strong>Signups &amp; users</strong>
          <p className="muted" style={{ margin: "6px 0 0", fontSize: 14 }}>
            New accounts appear under <strong>Users (new signups)</strong> in the sidebar after registration. Users stay{" "}
            <span className="badge badge-PENDING">PENDING_VERIFICATION</span> until email OTP is confirmed.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate("/admin/users?status=PENDING_VERIFICATION")}
            >
              View accounts pending email ({analytics?.pending_verification_users ?? 0})
            </button>
            <Link to="/admin/users">Open User Management</Link>
            <Link to="/join">Open public signup page</Link>
          </div>
        </div>
        <ul className="list">
          <li className="list-item"><span>Pending KYC Reviews</span><span className="badge badge-PENDING">{pendingKyc.length}</span></li>
          <li className="list-item">
            <span>Pending share redemptions (sell to treasury)</span>
            <span style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span className="badge badge-PENDING">{pendingRedemptionCountOverview}</span>
              {hasAdminModuleAccess(user, "OPERATIONS") ? (
                <Link to="/admin/control?tab=REDEMPTIONS">Open Redemptions</Link>
              ) : null}
            </span>
          </li>
          <li className="list-item">
            <span>Pending secondary transfers &amp; other share reviews</span>
            <span style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span className="badge badge-PENDING">{pendingOtherInvestmentRows.length}</span>
              {hasAdminModuleAccess(user, "OPERATIONS") && pendingOtherInvestmentRows.length > 0 ? (
                <Link to="/admin/control?tab=REDEMPTIONS">Open Redemptions</Link>
              ) : null}
            </span>
          </li>
          <li className="list-item"><span>Pending Profit Reviews</span><span className="badge badge-PENDING">{pendingPnl.length}</span></li>
        </ul>
      </div>
      )}

      {activeTab === "SETTINGS" && settings && (
        <form className="card stack" style={{ padding: 14 }} onSubmit={onSave}>
          <h3>Platform Settings</h3>
          <input
            value={settings.referral_rate_pct}
            onChange={(e) => setSettings({ ...settings, referral_rate_pct: e.target.value })}
            placeholder="Referral Rate %"
          />
          <label className="muted" style={{ fontSize: 13, display: "block" }}>
            Minimum share purchase
            <input
              value={settings.min_share_purchase ?? "0.10"}
              onChange={(e) => setSettings({ ...settings, min_share_purchase: e.target.value })}
              placeholder="e.g. 0.10"
            />
          </label>
          <label className="muted" style={{ fontSize: 13, display: "block" }}>
            Maximum ownership per investor (%)
            <input
              value={settings.max_share_ownership_pct ?? "20.00"}
              onChange={(e) => setSettings({ ...settings, max_share_ownership_pct: e.target.value })}
              placeholder="e.g. 20"
            />
          </label>
          <label className="muted" style={{ fontSize: 13, display: "block" }}>
            Transfer fee (% of notional shares×NAV)
            <input
              value={settings.share_transfer_fee_pct ?? "0.1"}
              onChange={(e) => setSettings({ ...settings, share_transfer_fee_pct: e.target.value })}
              placeholder="e.g. 0.1"
            />
            <small className="muted">
              Cash portion → revenue wallet; any share-component shortfall credits SSA (project cap unchanged).
            </small>
          </label>
          <label className="muted" style={{ fontSize: 13, display: "block" }}>
            Redemption fee (% of gross NAV per request — settled in shares to SSA)
            <input
              value={settings.redemption_fee_base_pct ?? settings.share_redemption_fee_pct ?? "0.2"}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  redemption_fee_base_pct: e.target.value,
                  share_redemption_fee_pct: e.target.value,
                })
              }
              placeholder="e.g. 0.2"
            />
            <small className="muted">
              Default 0.2% of gross per request; fee settles in shares to SSA (not cash). Daily pending-day fee is not applied.
            </small>
          </label>
          <label className="muted" style={{ fontSize: 13, display: "block" }}>
            Max redemption per request (% of holding)
            <input
              value={settings.max_redemption_pct_of_holdings ?? "1.0"}
              onChange={(e) => setSettings({ ...settings, max_redemption_pct_of_holdings: e.target.value })}
              placeholder="e.g. 1.0"
            />
          </label>
          <label className="muted" style={{ fontSize: 13, display: "block" }}>
            Min deposit request (BDT)
            <input
              value={settings.min_wallet_deposit_bdt ?? "100"}
              onChange={(e) => setSettings({ ...settings, min_wallet_deposit_bdt: e.target.value })}
              placeholder="Min Wallet Deposit"
            />
          </label>
          <label className="muted" style={{ fontSize: 13, display: "block" }}>
            Min wallet withdrawal (BDT)
            <input
              value={settings.min_wallet_withdrawal_bdt}
              onChange={(e) => setSettings({ ...settings, min_wallet_withdrawal_bdt: e.target.value })}
              placeholder="Min Wallet Withdrawal"
            />
          </label>
          {isSuperAdmin ? (
            <>
              <label className="muted" style={{ fontSize: 13, display: "block" }}>
                Wallet withdrawal fee (% of net payout)
                <input
                  value={settings.wallet_withdrawal_fee_pct ?? "0"}
                  onChange={(e) => setSettings({ ...settings, wallet_withdrawal_fee_pct: e.target.value })}
                  placeholder="e.g. 1"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                />
                <small className="muted">
                  Enter <strong>1</strong> for 1% of the bank payout (not 0.01). Use 0 for no percent fee.
                </small>
              </label>
              <label className="muted" style={{ fontSize: 13, display: "block" }}>
                Wallet withdrawal fee (fixed BDT per request)
                <input
                  value={settings.wallet_withdrawal_fee_fixed_bdt ?? "0"}
                  onChange={(e) => setSettings({ ...settings, wallet_withdrawal_fee_fixed_bdt: e.target.value })}
                  placeholder="e.g. 50"
                  type="number"
                  min="0"
                  step="0.01"
                />
                <small className="muted">
                  Flat fee added to each withdrawal (in addition to the percent fee). Debited from wallet with payout.
                </small>
              </label>
            </>
          ) : (
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              Wallet withdrawal fee: {settings.wallet_withdrawal_fee_pct ?? "0"}% + BDT{" "}
              {settings.wallet_withdrawal_fee_fixed_bdt ?? "0"} (Super Admin can edit in Settings).
            </p>
          )}
          <label className="settings-check-row">
            <input
              type="checkbox"
              checked={Boolean(settings.deposit_approval_remark_required)}
              onChange={(e) => setSettings({ ...settings, deposit_approval_remark_required: e.target.checked })}
            />
            <span>
              Require admin approval remark for <strong>deposit</strong> approvals (bulk and single).
            </span>
          </label>
          <label className="muted" style={{ fontSize: 13, display: "block" }}>
            P&amp;L admin fee (% of positive gross P&amp;L)
            <input
              value={settings.pnl_admin_fee_pct ?? "0"}
              onChange={(e) => setSettings({ ...settings, pnl_admin_fee_pct: e.target.value })}
              placeholder="e.g. 2.5"
              type="number"
              min="0"
              max="100"
              step="0.01"
            />
            <small className="muted">
              Applied to <strong>revenue − expenses</strong> when that amount is positive. Net P&amp;L = gross − fee.
            </small>
          </label>
          {isSuperAdmin && (
            <label className="muted" style={{ fontSize: 13, display: "block" }}>
              Asset manager commission (% of positive gross P&amp;L)
              <input
                value={settings.asset_manager_commission_pct ?? "0"}
                onChange={(e) => setSettings({ ...settings, asset_manager_commission_pct: e.target.value })}
                placeholder="e.g. 5"
                type="number"
                min="0"
                max="100"
                step="0.01"
              />
              <small className="muted">
                Paid to the active asset manager when a P&amp;L submission is <strong>approved</strong>. Set to 0 to
                disable. Only Super Admin can change this rate; it is snapshotted on each approval.
              </small>
            </label>
          )}
          <label className="muted" style={{ fontSize: 13, display: "block" }}>
            Default lock-in months (all funded projects)
            <input
              value={settings.default_lockin_months ?? 6}
              onChange={(e) => setSettings({ ...settings, default_lockin_months: e.target.value })}
              placeholder="e.g. 6"
              type="number"
              min="0"
              step="1"
            />
            <small className="muted">
              Same lock-in window for every project after full subscription: fundraising end + these months. Redemptions
              unlock after this period; transfers stay allowed during lock-in.
            </small>
          </label>
          <label className="muted" style={{ fontSize: 13, display: "block" }}>
            Fundraising & secondary statuses (comma-separated)
            <input
              value={settings.fundraising_secondary_statuses || ""}
              onChange={(e) => setSettings({ ...settings, fundraising_secondary_statuses: e.target.value })}
              placeholder="FUNDING,PAUSED"
            />
            <small className="muted">
              Example: FUNDING,PAUSED. Use uppercase status codes separated by commas.
            </small>
          </label>
          <label className="muted" style={{ fontSize: 13, display: "block" }}>
            Secondary market statuses (comma-separated)
            <input
              value={settings.secondary_market_statuses || ""}
              onChange={(e) => setSettings({ ...settings, secondary_market_statuses: e.target.value })}
              placeholder="LOCK_IN"
            />
            <small className="muted">
              Example: LOCK_IN. Funded projects are available for secondary share activities.
            </small>
          </label>
          <label className="muted" style={{ fontSize: 13, display: "block" }}>
            Funded overview statuses (comma-separated)
            <input
              value={settings.funded_overview_statuses || ""}
              onChange={(e) => setSettings({ ...settings, funded_overview_statuses: e.target.value })}
              placeholder="LOCK_IN"
            />
            <small className="muted">
              Example: LOCK_IN. Supported project statuses: DRAFT, REVIEW, PAUSED, FUNDING, LOCK_IN, MATURED, REDEEMED, CLOSED, CANCELLED.
            </small>
          </label>
          <button type="submit">Save Settings</button>
        </form>
      )}

      {activeTab === "USERS" && (
      <div className="card stack" style={{ padding: 14 }}>
          <h3>User Management</h3>
          <p className="muted" style={{ marginTop: 0, fontSize: 14 }}>
            Matches records in the database. Use KYC tab for document review.{" "}
            <Link to="/join">Public signup</Link> · <Link to="/admin/users">Refresh this page</Link>
          </p>
          <div className="grid-2">
            <input placeholder="Search name, email, or phone" value={userFilter.q} onChange={(e) => setUserFilter({ ...userFilter, q: e.target.value })} />
            <select value={userFilter.status} onChange={(e) => setUserFilter({ ...userFilter, status: e.target.value })}>
              <option value="">All account statuses</option>
              <option value="ACTIVE">ACTIVE</option>
              <option value="PENDING_VERIFICATION">PENDING_VERIFICATION</option>
              <option value="SUSPENDED">SUSPENDED</option>
              <option value="BLOCKED">BLOCKED</option>
            </select>
          </div>
          <div className="grid-2" style={{ marginTop: 8 }}>
            <select value={userFilter.kyc_status} onChange={(e) => setUserFilter({ ...userFilter, kyc_status: e.target.value })}>
              <option value="">All KYC statuses</option>
              <option value="NOT_SUBMITTED">NOT_SUBMITTED</option>
              <option value="PENDING">PENDING</option>
              <option value="APPROVED">APPROVED</option>
              <option value="REJECTED">REJECTED</option>
            </select>
            <span />
          </div>
          <button className="btn-secondary" onClick={() => load()}>Apply Filters</button>
          <div className="table-wrap">
            <table className="ops-table">
              <thead>
                <tr><th>Name</th><th>Email</th><th>Account No</th><th>Role</th><th>Status</th><th>KYC</th><th>Referral</th><th>Action</th></tr>
              </thead>
              <tbody>
                {paginate(users, pages.users, 10).map((u) => (
                  <tr key={u.id}>
                    <td>{u.full_name || "—"}</td>
                    <td>
                      {u.email ? (
                        <a href={`mailto:${u.email}`} title="Email user">{u.email}</a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {u.account_number || "-"}
                      {u.account_number === "00" && <span className="badge" style={{ marginLeft: 8 }}>COMPANY ROOT</span>}
                      {u.account_number === "01" && <span className="badge" style={{ marginLeft: 8 }}>DEFAULT SPONSOR</span>}
                    </td>
                    <td>{u.role}</td>
                    <td><span className={`badge badge-${u.status}`}>{u.status}</span></td>
                    <td><span className={`badge badge-${u.kyc_status === "APPROVED" ? "APPROVED" : u.kyc_status === "REJECTED" ? "REJECTED" : "PENDING"}`}>{u.kyc_status || "—"}</span></td>
                    <td style={{ fontSize: 13 }} title="Referrer account number">{u.referred_by_account_number || "—"}</td>
                    <td>
                      {u.account_number === "00" ? (
                        <button className="btn-secondary" disabled>Protected</button>
                      ) : (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {u.status === "ACTIVE" ? (
                            <>
                              <button className="btn-danger" onClick={() => runAdminAction(() => suspendUser(u.id), "User suspended.", "Failed to suspend user.")}>Suspend</button>
                              <button className="btn-secondary" onClick={() => runAdminAction(() => blockUser(u.id), "User blocked.", "Failed to block user.")}>Block</button>
                            </>
                          ) : (
                            <button onClick={() => runAdminAction(() => reinstateUser(u.id), "User reinstated.", "Failed to reinstate user.")}>Reinstate</button>
                          )}
                          {isSuperAdmin && ["ADMIN", "ACCOUNTANT", "MARKETING"].includes(String(u.role || "").toUpperCase()) && (
                            <button className="btn-secondary" onClick={() => openPermissionsModal(u)}>Permissions</button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls page={pages.users} total={users.length} pageSize={10} onPageChange={(v) => setPage("users", v)} />
        </div>
      )}

      {activeTab === "ASSET_MANAGERS" && (
      <div className="stack">
        <div className="card stack" style={{ padding: 14 }}>
          <h3>Staff & Asset Managers</h3>
          <p className="muted" style={{ marginTop: 0, fontSize: 14 }}>
            Super Admin can create General Admin, Accountant, Marketing, and Asset Manager accounts from this panel.
            Each email and mobile number can be used only once on the whole platform (not shared with investors or other staff).
          </p>
          {!isSuperAdmin ? (
            <p className="muted" style={{ marginBottom: 0 }}>
              Only Super Admin can create staff accounts.
            </p>
          ) : (
            <form className="stack" onSubmit={onCreateAssetManager}>
              <div className="form-field">
                <select
                  value={assetManagerForm.role}
                  onChange={(e) => setAssetManagerForm((prev) => ({ ...prev, role: e.target.value }))}
                  required
                >
                  {STAFF_ROLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {assetManagerSubmitAttempted && assetManagerErrors.role && (
                  <small className="field-error">{assetManagerErrors.role}</small>
                )}
              </div>
              <div className="grid-2">
                <div className="form-field">
                  <input
                    placeholder="Full name"
                    value={assetManagerForm.full_name}
                    onChange={(e) => setAssetManagerForm((prev) => ({ ...prev, full_name: e.target.value }))}
                    required
                  />
                  {assetManagerSubmitAttempted && assetManagerErrors.full_name && (
                    <small className="field-error">{assetManagerErrors.full_name}</small>
                  )}
                </div>
                <div className="form-field">
                  <input
                    type="email"
                    placeholder="Email"
                    value={assetManagerForm.email}
                    onChange={(e) => setAssetManagerForm((prev) => ({ ...prev, email: e.target.value }))}
                    required
                  />
                  {assetManagerSubmitAttempted && assetManagerErrors.email && (
                    <small className="field-error">{assetManagerErrors.email}</small>
                  )}
                </div>
              </div>
              <div className="grid-2">
                <div className="form-field">
                  <div className="phone-with-code">
                    <select
                      value={assetManagerForm.country_code}
                      onChange={(e) => setAssetManagerForm((prev) => ({ ...prev, country_code: e.target.value }))}
                      required
                    >
                      {COUNTRY_CODE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <input
                      placeholder="Mobile number"
                      value={assetManagerForm.phone_number}
                      onChange={(e) => setAssetManagerForm((prev) => ({ ...prev, phone_number: e.target.value }))}
                      required
                    />
                  </div>
                  {assetManagerSubmitAttempted && (assetManagerErrors.country_code || assetManagerErrors.phone_number) && (
                    <small className="field-error">{assetManagerErrors.country_code || assetManagerErrors.phone_number}</small>
                  )}
                </div>
                <div className="form-field">
                  <select
                    value={assetManagerForm.nationality}
                    onChange={(e) => setAssetManagerForm((prev) => ({ ...prev, nationality: e.target.value }))}
                    required
                  >
                    {NATIONALITY_OPTIONS.map((nat) => (
                      <option key={nat} value={nat}>{nat}</option>
                    ))}
                  </select>
                  {assetManagerSubmitAttempted && assetManagerErrors.nationality && (
                    <small className="field-error">{assetManagerErrors.nationality}</small>
                  )}
                </div>
              </div>
              <div className="form-field">
                <div className="password-field">
                    <input
                      type={showAssetManagerPassword ? "text" : "password"}
                      placeholder="Temporary password (min 8 chars, letters + numbers)"
                      value={assetManagerForm.password}
                      onChange={(e) => setAssetManagerForm((prev) => ({ ...prev, password: e.target.value }))}
                      minLength={8}
                      required
                    />
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setShowAssetManagerPassword((prev) => !prev)}
                      aria-label={showAssetManagerPassword ? "Hide password" : "Show password"}
                    >
                      {showAssetManagerPassword ? "Hide" : "Show"}
                    </button>
                </div>
                {assetManagerSubmitAttempted && assetManagerErrors.password && (
                  <small className="field-error">{assetManagerErrors.password}</small>
                )}
              </div>
              <div>
                <button type="submit" disabled={!isSuperAdmin || assetManagerSubmitting}>
                  {assetManagerSubmitting ? "Creating..." : "Create User"}
                </button>
                {assetManagerSubmitError && <p className="msg msg-error" style={{ marginTop: 8 }}>{assetManagerSubmitError}</p>}
              </div>
            </form>
          )}
        </div>

        <div className="card stack" style={{ padding: 14 }}>
          <h4 style={{ marginTop: 0 }}>Existing Staff Accounts</h4>
          <div className="table-wrap">
            <table className="ops-table">
              <thead>
                <tr><th>Name</th><th>Email</th><th>Role</th><th>Phone</th><th>Nationality</th><th>Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {paginate(managedUsers, pages.assetManagers, 10).map((u) => (
                  <tr key={u.id}>
                    <td>{u.full_name || "—"}</td>
                    <td>{u.email || "—"}</td>
                    <td>{u.role || "—"}</td>
                    <td>{u.phone || "—"}</td>
                    <td>{u.nationality || "—"}</td>
                    <td><span className={`badge badge-${u.status}`}>{u.status}</span></td>
                    <td>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {u.status === "ACTIVE" ? (
                          <>
                            <button className="btn-danger" onClick={() => runAdminAction(() => suspendUser(u.id), "User suspended.", "Failed to suspend user.")}>Suspend</button>
                            <button className="btn-secondary" onClick={() => runAdminAction(() => blockUser(u.id), "User blocked.", "Failed to block user.")}>Block</button>
                          </>
                        ) : (
                          <button onClick={() => runAdminAction(() => reinstateUser(u.id), "User reinstated.", "Failed to reinstate user.")}>Reinstate</button>
                        )}
                        {isSuperAdmin && ["ADMIN", "ACCOUNTANT", "MARKETING"].includes(String(u.role || "").toUpperCase()) && (
                          <button className="btn-secondary" onClick={() => openPermissionsModal(u)}>Permissions</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {managedUsers.length === 0 && (
                  <tr>
                    <td colSpan={7} className="muted">No staff accounts found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <PaginationControls
            page={pages.assetManagers}
            total={managedUsers.length}
            pageSize={10}
            onPageChange={(v) => setPage("assetManagers", v)}
          />
        </div>
      </div>
      )}

      {activeTab === "WALLET_TXNS" && (
      <div className="stack">
        <div className="card" style={{ padding: 14 }}>
          <h3>Deposits</h3>
          <p className="muted" style={{ marginTop: 0, fontSize: 14 }}>
            Match bank/mobile proofs to references below. Approve to credit wallet. Use checkboxes for bulk actions (FR-ADM-003).
            {settings?.deposit_approval_remark_required ? (
              <span>
                {" "}
                <strong>Admin remark is required</strong> on each approval (platform setting).
              </span>
            ) : null}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10, alignItems: "center" }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={async () => {
                setMessage("");
                try {
                  await downloadDepositsReportCsv();
                  setMessage("Deposits CSV downloaded.");
                } catch (err) {
                  setMessage(getErrorMessage(err, "Export failed."));
                }
              }}
            >
              Export deposits CSV
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={async () => {
                setMessage("");
                try {
                  await downloadDepositsReportPdf();
                  setMessage("Deposits PDF downloaded.");
                } catch (err) {
                  setMessage(getErrorMessage(err, "PDF export failed."));
                }
              }}
            >
              Export deposits PDF
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={pendingDepositsOnPage.length === 0}
              onClick={() => setSelectedDepositIds(pendingDepositsOnPage.map((d) => String(d.id)))}
            >
              Select pending on page
            </button>
            <button type="button" className="btn-secondary" disabled={selectedDepositIds.length === 0} onClick={() => setSelectedDepositIds([])}>
              Clear selection
            </button>
            <button
              type="button"
              disabled={selectedDepositIds.length === 0}
              onClick={() => {
                if (selectedDepositIds.length === 0) return;
                openDepositApproveModal("bulk", "", selectedDepositIds, `${selectedDepositIds.length} deposit(s)`);
              }}
            >
              Approve selected ({selectedDepositIds.length})
            </button>
            <button
              type="button"
              className="btn-danger"
              disabled={selectedDepositIds.length === 0}
              onClick={() =>
                openRejectModal("deposit_bulk_reject", "", `Reject ${selectedDepositIds.length} deposit(s)`, selectedDepositIds)
              }
            >
              Reject selected…
            </button>
          </div>
          <div className="table-wrap">
            <table className="ops-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}> </th>
                  <th>User</th>
                  <th>Amount</th>
                  <th>Channel</th>
                  <th>Reference</th>
                  <th>Txn date</th>
                  <th>Proof</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginate(deposits, pages.deposits, 10).map((d) => (
                  <tr key={d.id}>
                    <td>
                      {d.status === "PENDING" ? (
                        <input
                          type="checkbox"
                          checked={selectedDepositIds.includes(String(d.id))}
                          onChange={() => toggleDepositSelect(d.id)}
                          aria-label={`Select deposit ${d.id}`}
                        />
                      ) : (
                        " "
                      )}
                    </td>
                    <td>
                      <div>{d.user_name || "—"}</div>
                      <div style={{ fontSize: 12 }} className="muted">{d.user_email || ""}</div>
                    </td>
                    <td>BDT {d.amount}</td>
                    <td>{d.bank_channel}</td>
                    <td style={{ fontSize: 13 }}>{d.transaction_ref}</td>
                    <td>{d.transaction_date}</td>
                    <td>
                      {d.screenshot_url ? (
                        <button type="button" className="btn-secondary" onClick={() => openDepositProof(d)}>
                          Doc View
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td><span className={`badge badge-${d.status}`}>{d.status}</span></td>
                    <td>
                      {d.status === "PENDING" ? (
                        <span style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          {d.screenshot_url ? (
                            <button type="button" className="btn-secondary" onClick={() => openDepositProof(d)}>
                              Doc View
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={() =>
                              openDepositApproveModal("single", d.id, [], `BDT ${money(d.amount)}`)
                            }
                          >
                            Approve
                          </button>
                          <button className="btn-danger" onClick={() => openRejectModal("deposit_reject", d.id, "Reject Deposit")}>Reject</button>
                        </span>
                      ) : d.screenshot_url ? (
                        <button type="button" className="btn-secondary" onClick={() => openDepositProof(d)}>
                          Doc View
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls page={pages.deposits} total={deposits.length} pageSize={10} onPageChange={(v) => setPage("deposits", v)} />
        </div>
        <div className="card" style={{ padding: 14 }}>
          <h3>Withdrawals</h3>
          <div className="table-wrap">
            <table className="ops-table">
              <thead><tr><th>Amount</th><th>Status</th><th>Proof</th><th>Action</th></tr></thead>
              <tbody>
                {paginate(withdrawals, pages.withdrawals, 10).map((w) => (
                  <tr key={w.id}>
                    <td>BDT {w.amount}</td>
                    <td><span className={`badge badge-${w.status}`}>{w.status}</span></td>
                    <td>
                      {(w.payout_proof_url || withdrawalProofRefs[String(w.id)]) ? (
                        <button type="button" className="btn-secondary" onClick={() => openWithdrawalProof(w)}>
                          Doc View
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {w.status === "PENDING" && (
                        <span style={{ display: "flex", gap: 8 }}>
                          <button onClick={() => runAdminAction(() => reviewAdminWithdrawal(w.id, { status: "PROCESSING" }), "Withdrawal marked processing.", "Failed to process withdrawal.")}>Process</button>
                          <button className="btn-danger" onClick={() => openRejectModal("withdraw_reject", w.id, "Reject Withdrawal")}>Reject</button>
                        </span>
                      )}
                      {w.status === "PROCESSING" && (
                        <span style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                          <input
                            style={{ width: 210, padding: 6, fontSize: 12 }}
                            type="file"
                            accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                            onChange={(e) => uploadWithdrawalProof(w.id, e.target.files?.[0])}
                          />
                          <button
                            className="btn-secondary"
                            disabled={withdrawalProofBusy[String(w.id)]}
                            onClick={() => openWithdrawalProof(w)}
                          >
                            {withdrawalProofBusy[String(w.id)] ? "Uploading..." : "View/Check proof"}
                          </button>
                          <button
                            onClick={() =>
                              runAdminAction(
                                () =>
                                  reviewAdminWithdrawal(w.id, {
                                    status: "COMPLETED",
                                    payout_proof_url: String(w.payout_proof_url || withdrawalProofRefs[String(w.id)] || ""),
                                  }),
                                "Withdrawal completed.",
                                "Failed to complete withdrawal."
                              )
                            }
                          >
                            Complete
                          </button>
                          <button className="btn-danger" onClick={() => openRejectModal("withdraw_fail", w.id, "Fail Withdrawal")}>Fail</button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls page={pages.withdrawals} total={withdrawals.length} pageSize={10} onPageChange={(v) => setPage("withdrawals", v)} />
        </div>
      </div>
      )}

      {activeTab === "WALLET_TXNS" && (
      <div className="card" style={{ padding: 14 }}>
        <h3>Transaction Logs</h3>
        <div className="grid-2">
          <select value={txnFilter.direction} onChange={(e) => setTxnFilter({ ...txnFilter, direction: e.target.value })}>
            <option value="">All Directions</option>
            <option value="CREDIT">CREDIT</option>
            <option value="DEBIT">DEBIT</option>
          </select>
          <select value={txnFilter.type} onChange={(e) => setTxnFilter({ ...txnFilter, type: e.target.value })}>
            <option value="">All Types</option>
            <option value="DEPOSIT">DEPOSIT</option>
            <option value="WITHDRAWAL">WITHDRAWAL</option>
            <option value="TRANSFER">TRANSFER</option>
            <option value="REFUND">REFUND</option>
            <option value="ADJUSTMENT">ADJUSTMENT</option>
            <option value="INVESTMENT">Share purchased</option>
          </select>
        </div>
        <div className="table-wrap">
          <table className="ops-table">
            <thead><tr><th>Type</th><th>Direction</th><th>Amount</th><th>User</th></tr></thead>
            <tbody>
              {paginate(transactions, pages.txns, 12).map((t) => (
                <tr key={t.id}>
                  <td>{walletTransactionTypeLabel(t.type, t)}</td>
                  <td><span className={`badge badge-${t.direction}`}>{t.direction}</span></td>
                  <td>BDT {t.amount}</td>
                  <td>{t.user_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationControls page={pages.txns} total={transactions.length} pageSize={12} onPageChange={(v) => setPage("txns", v)} />
      </div>
      )}

      {activeTab === "KYC" && (
      <div className="card" style={{ padding: 14 }}>
        <h3>KYC Management</h3>
        <div className="table-wrap">
          <table className="ops-table">
            <thead><tr><th>Document Type</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>
              {paginate(kycItems, pages.kyc, 10).map((k) => (
                <tr key={k.id}>
                  <td>{k.document_type}</td>
                  <td><span className={`badge badge-${k.status}`}>{k.status}</span></td>
                  <td>
                    {k.status === "PENDING" ? (
                      <span style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => runAdminAction(() => reviewAdminKyc(k.id, { status: "APPROVED" }), "KYC approved.", "Failed to approve KYC.")}>Approve</button>
                        <button className="btn-danger" onClick={() => openRejectModal("kyc_reject", k.id, "Reject KYC")}>Reject</button>
                      </span>
                    ) : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationControls page={pages.kyc} total={kycItems.length} pageSize={10} onPageChange={(v) => setPage("kyc", v)} />
      </div>
      )}

      {activeTab === "PROJECTS" && (
      <div className="card stack admin-projects-panel" style={{ padding: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3>Projects</h3>
          <button
            className="compact-btn"
            type="button"
            onClick={() => {
              setProjectCreateError("");
              setProjectCreateSubmitting(false);
              setProjectModalOpen(true);
            }}
          >
            Create Project
          </button>
        </div>
        <div className="card" style={{ padding: 10, background: "var(--surface-alt)" }}>
          <strong>Project lifecycle legend</strong>
          <p className="muted" style={{ margin: "6px 0 0", fontSize: 13 }}>
            Draft → Review → Fundraising → Funded (target reached) → Redeemed / closed (P&amp;L finalized and shareholders settled by Super Admin)
          </p>
          <p className="muted" style={{ margin: "6px 0 0", fontSize: 13 }}>
            Funded projects are lifecycle-protected: no cancellation or manual stage changes; lock-in blocks redemptions until it
            ends; then investors may request redemption. The project becomes Redeemed only after 100% of issued shares are redeemed to treasury.
          </p>
        </div>
        <p className="muted admin-projects-intro">
          Use <strong>Edit</strong> for draft and review projects. Use <strong>Update</strong> on live projects to publish a performance PDF for investors.
        </p>

        <section className="admin-projects-section">
        <h3 className="admin-projects-section__title">Draft &amp; review queue</h3>
        <div
          className="table-wrap ops-table-wrap--admin-projects"
          role="region"
          aria-label="Draft and review projects"
          tabIndex={0}
        >
          <table className="ops-table ops-table--admin-projects">
            <thead>
              <tr>
                <th className="ops-table__col--sl">SL.</th>
                <th className="ops-table__col--code">Project</th>
                <th className="ops-table__col--particulars">Particulars</th>
                <th className="ops-table__col--attachments">Attachments</th>
                <th className="ops-table__col--status">Status</th>
                <th className="ops-table__col--return" title="Estimated return per annum">
                  Est. return (p.a.)
                </th>
                <th className="ops-table__cell--actions">Controls</th>
              </tr>
            </thead>
            <tbody>
              {draftOpenRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="admin-projects-empty">
                    No projects in draft or review.
                  </td>
                </tr>
              ) : (
              draftOpenRows.map((p, idx) => (
                <tr key={`early-${p.id}`}>
                  <td className="ops-table__col--sl">{String(idx + 1).padStart(2, "0")}</td>
                  <td className="ops-table__col--code">
                    <span className="admin-project-code">{formatProjectCode(p.project_code) || "—"}</span>
                  </td>
                  <td className="ops-table__col--particulars">
                    <AdminProjectParticularsCell project={p} />
                  </td>
                  <td className="ops-table__col--attachments">
                    <AdminProjectAttachmentsCell project={p} onViewPdf={openProjectOverviewPdf} />
                  </td>
                  <td className="ops-table__col--status">
                    <span className={`badge badge-${normalizeProjectStatus(p.status)}`}>{statusLabel(p.status)}</span>
                  </td>
                  <td className="ops-table__col--return admin-project-return">
                    {Number(p.expected_return_pct || 0).toFixed(2)}%
                  </td>
                  <td className="ops-table__cell--actions">
                    <div className="admin-project-controls">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => openProjectEditModal(p)}
                        title={adminProjectControlAction(getStageKey(p)).title}
                      >
                        {adminProjectControlAction(getStageKey(p)).label}
                      </button>
                      <button type="button" className="btn-secondary" onClick={() => openLandingModal(p)}>
                        Landing
                      </button>
                      <AdminProjectPreviewButtons project={p} />
                      {isSuperAdmin && (
                        <button type="button" onClick={() => openProjectStageModal(p)}>
                          {normalizeProjectStatus(p.status) === "CANCELLED" ? "Restart as Draft" : "Update Stage"}
                        </button>
                      )}
                      {normalizeProjectStatus(p.status) === "DRAFT" && (
                        <button type="button" className="btn-danger compact-btn" onClick={() => handleDiscardDraftProject(p)}>
                          Discard draft
                        </button>
                      )}
                      {isSuperAdmin && normalizeProjectStatus(p.status) === "REVIEW" && (
                        <>
                          <button type="button" className="compact-btn" onClick={() => handlePauseProject(p)}>
                            Pause listing
                          </button>
                          <button
                            type="button"
                            className="btn-danger compact-btn"
                            onClick={() => handleCancelListedProject(p)}
                          >
                            Cancel project
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
              )}
            </tbody>
          </table>
        </div>
        </section>

        <section className="admin-projects-section">
        <div className="admin-projects-toolbar">
          <h3 className="admin-projects-section__title">Fundraising &amp; funded projects</h3>
          <select
            className="compact-field admin-projects-filter"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            aria-label="Filter projects by stage"
          >
            <option value="ALL">All projects</option>
            <option value="FUNDRAISING">Fundraising</option>
            <option value="FUNDED">Funded</option>
          </select>
        </div>
        <p className="admin-projects-scroll-hint">Scroll horizontally to see financial columns and controls.</p>
        <div
          className="table-wrap ops-table-wrap--compact-fin ops-table-wrap--admin-projects"
          role="region"
          aria-label="Fundraising and funded projects"
          tabIndex={0}
        >
          <table className="ops-table ops-table--projects ops-table--compact-fin ops-table--split-zones">
            <thead>
              <tr>
                <AdminProjectsMarketTableHead actionsLabel="Controls" />
              </tr>
            </thead>
            <tbody>
              {marketRowsFiltered.length === 0 ? (
                <tr>
                  <td colSpan={17} className="admin-projects-empty">
                    No projects match this filter.
                  </td>
                </tr>
              ) : (
              paginate(marketRowsFiltered, pages.projects, 10).map((p, idx) => {
                const projectControl = adminProjectControlAction(getStageKey(p));
                const finClosed = normalizeProjectStatus(p.status) === "CLOSED";
                return (
                <tr key={p.id} className={finClosed ? "ops-table__row--closed" : undefined}>
                  <td className="ops-table__cell--identity ops-table__col--sl">{String((pages.projects - 1) * 10 + idx + 1).padStart(2, "0")}</td>
                  <td className="ops-table__cell--identity ops-table__col--code">
                    <span className="admin-project-code">{formatProjectCode(p.project_code) || "—"}</span>
                  </td>
                  <td className="ops-table__cell--identity ops-table__col--particulars">
                    <AdminProjectParticularsCell project={p} />
                  </td>
                  <td className="ops-table__cell--identity ops-table__col--attachments">
                    <AdminProjectAttachmentsCell project={p} onViewPdf={openProjectOverviewPdf} />
                  </td>
                  <td className="ops-table__cell--identity ops-table__col--status">
                    <span className={`badge badge-${normalizeProjectStatus(p.status)}`}>{statusLabel(p.status)}</span>
                  </td>
                  <td className="ops-table__cell--identity ops-table__col--supply">
                    <AdminShareSupplySummary project={p} />
                  </td>
                  <td className="admin-project-return ops-table__cell--metrics ops-table__col--return">
                    {Number(p.expected_return_pct || 0).toFixed(2)}%
                  </td>
                  <AdminProjectFinancialDataCells project={p} pnlTotalsByProject={pnlTotalsByProject} />
                  <td className="ops-table__cell--actions">
                    <div className="admin-project-controls">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => openProjectEditModal(p)}
                        title={projectControl.title}
                      >
                        {projectControl.label}
                      </button>
                      <button type="button" className="btn-secondary" onClick={() => openLandingModal(p)}>
                        Landing
                      </button>
                      <AdminProjectPreviewButtons project={p} />
                      {isSuperAdmin &&
                        !["FUNDRAISING", "FUNDED", "TERMINAL"].includes(getStageKey(p)) && (
                        <button onClick={() => openProjectStageModal(p)}>
                          {getStageKey(p) === "CANCELLED" ? "Restart as Draft" : "Update Stage"}
                        </button>
                      )}
                      {isSuperAdmin && canShowExtendAction(p) && (
                        <button className="btn-secondary" onClick={() => handleProjectExtend(p.id)}>
                          Extend 7 Days
                        </button>
                      )}
                      {isSuperAdmin && normalizeProjectStatus(p.status) === "REVIEW" && (
                        <button type="button" className="compact-btn" onClick={() => handlePauseProject(p)}>
                          Pause listing
                        </button>
                      )}
                      {isSuperAdmin && normalizeProjectStatus(p.status) === "PAUSED" && (
                        <button type="button" className="compact-btn" onClick={() => handleResumeProject(p)}>
                          Resume listing
                        </button>
                      )}
                      {isSuperAdmin && ["REVIEW", "FUNDING", "PAUSED"].includes(normalizeProjectStatus(p.status)) && (
                        <button
                          type="button"
                          className="btn-danger compact-btn"
                          onClick={() => handleCancelListedProject(p)}
                        >
                          {Number(p?.shares_sold || 0) > 0 ? "Emergency cancel" : "Cancel project"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })
              )}
            </tbody>
          </table>
        </div>
        <PaginationControls page={pages.projects} total={marketRowsFiltered.length} pageSize={10} onPageChange={(v) => setPage("projects", v)} />
        </section>

        <section className="admin-projects-section">
        <h3 className="admin-projects-section__title">Redeemed &amp; closed projects</h3>
        <p className="muted admin-projects-section__lead">
          After full SSA redemption or terminal closure. Use <strong>Update</strong> for performance PDFs; super admins may <strong>Close</strong> redeemed projects when finished.
        </p>
        <p className="admin-projects-scroll-hint">Scroll horizontally to see all columns.</p>
        <div
          className="table-wrap ops-table-wrap--compact-fin ops-table-wrap--admin-projects"
          role="region"
          aria-label="Redeemed and closed projects"
          tabIndex={0}
        >
          <table className="ops-table ops-table--compact-fin ops-table--split-zones">
            <thead>
              <tr>
                <AdminProjectsMarketTableHead actionsLabel="Actions" />
              </tr>
            </thead>
            <tbody>
              {redeemedAndClosedRows.length === 0 ? (
                <tr>
                  <td colSpan={17} className="admin-projects-empty">
                    No redeemed or closed projects yet.
                  </td>
                </tr>
              ) : (
                paginate(redeemedAndClosedRows, pages.redeemedClosed, 10).map((p, idx) => (
                <tr
                  key={`completed-${p.id}`}
                  className={normalizeProjectStatus(p.status) === "CLOSED" ? "ops-table__row--closed" : undefined}
                >
                  <td className="ops-table__cell--identity ops-table__col--sl">{String((pages.redeemedClosed - 1) * 10 + idx + 1).padStart(2, "0")}</td>
                  <td className="ops-table__cell--identity ops-table__col--code">
                    <span className="admin-project-code">{formatProjectCode(p.project_code) || "—"}</span>
                  </td>
                  <td className="ops-table__cell--identity ops-table__col--particulars">
                    <AdminProjectParticularsCell project={p} />
                  </td>
                  <td className="ops-table__cell--identity ops-table__col--attachments">
                    <AdminProjectAttachmentsCell project={p} onViewPdf={openProjectOverviewPdf} />
                  </td>
                  <td className="ops-table__cell--identity ops-table__col--status">
                    <span className={`badge badge-${normalizeProjectStatus(p.status)}`}>{statusLabel(p.status)}</span>
                  </td>
                  <td className="ops-table__cell--identity ops-table__col--supply">
                    <AdminShareSupplySummary project={p} />
                  </td>
                  <td className="admin-project-return ops-table__cell--metrics ops-table__col--return">
                    {Number(p.expected_return_pct || 0).toFixed(2)}%
                  </td>
                  <AdminProjectFinancialDataCells project={p} pnlTotalsByProject={pnlTotalsByProject} />
                  <td className="ops-table__cell--actions">
                    <div className="admin-project-controls">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => openProjectEditModal(p)}
                        title={adminProjectControlAction(getStageKey(p)).title}
                      >
                        {adminProjectControlAction(getStageKey(p)).label}
                      </button>
                      <button type="button" className="btn-secondary" onClick={() => openLandingModal(p)}>
                        Landing
                      </button>
                      <AdminProjectPreviewButtons project={p} />
                      {isSuperAdmin && normalizeProjectStatus(p.status) === "REDEEMED" ? (
                        <button
                          type="button"
                          className="btn-danger compact-btn"
                          onClick={() => handleCloseRedeemedProject(p)}
                        >
                          Close
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
              )}
            </tbody>
          </table>
        </div>
        <PaginationControls
          page={pages.redeemedClosed}
          total={redeemedAndClosedRows.length}
          pageSize={10}
          onPageChange={(v) => setPage("redeemedClosed", v)}
        />
        </section>

        <section className="admin-projects-section">
        <h3 className="admin-projects-section__title">Cancelled projects</h3>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          Draft or open voids use cancellation kind &quot;VOID_PRE_FUNDRAISE&quot; and are hidden from investors. Listing cancellations remain visible to investors where applicable.
        </p>
        <div className="table-wrap">
          <table className="ops-table">
            <thead>
              <tr>
                <th>SL.</th>
                <th>Project</th>
                <th>Particulars</th>
                <th>Kind</th>
                <th>Est. Return (p.a.)</th>
              </tr>
            </thead>
            <tbody>
              {cancelledRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="muted">
                    No cancelled projects.
                  </td>
                </tr>
              ) : (
                cancelledRows.map((p, idx) => (
                  <tr key={`cancelled-${p.id}`}>
                    <td>{String(idx + 1).padStart(2, "0")}</td>
                    <td>{p.project_code || "—"}</td>
                    <td>{projectTitleWithCode(p)}</td>
                    <td style={{ fontSize: 13 }}>{p.cancellation_kind || "—"}</td>
                    <td>{p.expected_return_pct}%</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </section>

        <h4 style={{ marginTop: 12 }}>Assigned Projects</h4>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          Each project has at most one active asset manager (PNL authority). Unassign before changing manager.
        </p>
        <div className="table-wrap">
          <table className="ops-table">
            <thead><tr><th>SL.</th><th>Project</th><th>Asset Manager</th><th>Action</th></tr></thead>
            <tbody>
              {paginate(assignedManagerRows, pages.assignedManagers, 10).map((p, idx) => (
                <tr key={`assigned-am-${p.id}`}>
                  <td>{String((pages.assignedManagers - 1) * 10 + idx + 1).padStart(2, "0")}</td>
                  <td>{projectTitleWithCode(p)}</td>
                  <td>{assetManagerNameById[p.active_asset_manager] || p.active_asset_manager}</td>
                  <td>
                    <button type="button" className="btn-danger compact-btn" onClick={() => handleUnassignManager(p.id)}>
                      Unassign
                    </button>
                  </td>
                </tr>
              ))}
              {assignedManagerRows.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted">No projects with an active asset manager.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <PaginationControls
          page={pages.assignedManagers}
          total={assignedManagerRows.length}
          pageSize={10}
          onPageChange={(v) => setPage("assignedManagers", v)}
        />
        <h4 style={{ marginTop: 20 }}>Assign Asset Manager</h4>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          Unassigned projects in <strong>Fundraising</strong> or <strong>Funded</strong> only.
        </p>
        <div className="table-wrap">
          <table className="ops-table">
            <thead><tr><th>SL.</th><th>Project</th><th>Asset Manager</th><th>Action</th></tr></thead>
            <tbody>
              {paginate(unassignedManagerRows, pages.assignments, 10).map((p, idx) => (
                <tr key={`assign-${p.id}`}>
                  <td>{String((pages.assignments - 1) * 10 + idx + 1).padStart(2, "0")}</td>
                  <td>{projectTitleWithCode(p)}</td>
                  <td>
                    <select
                      className="compact-field"
                      value={managerAssignments[p.id] || ""}
                      onChange={(e) => setManagerAssignments({ ...managerAssignments, [p.id]: e.target.value })}
                    >
                      <option value="">Select manager</option>
                      {assetManagers.map((m) => (
                        <option key={m.id} value={m.id}>{m.full_name}</option>
                      ))}
                    </select>
                  </td>
                  <td><button type="button" className="btn-secondary compact-btn" onClick={() => handleAssignManager(p.id)}>Assign</button></td>
                </tr>
              ))}
              {unassignedManagerRows.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted">All eligible projects already have an asset manager.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <PaginationControls page={pages.assignments} total={unassignedManagerRows.length} pageSize={10} onPageChange={(v) => setPage("assignments", v)} />
      </div>
      )}

      {activeTab === "PROFIT" && (
      <div className="stack">
        <div className="card" style={{ padding: 14 }}>
          <h3>Pending P&amp;L reviews</h3>
          <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
            Asset manager submissions awaiting approval. Revenue, expenses, and fund value apply to the whole project for the period.
          </p>
          <div className="table-wrap">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Period</th>
                  <th>Revenue</th>
                  <th>Expenses</th>
                  <th>Admin fee</th>
                  <th>Net P&amp;L</th>
                  <th>Fund after P&amp;L</th>
                  <th>Submitted</th>
                  <th>Asset manager</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginate(pendingPnl, pages.pnl, 10).map((p) => (
                  <tr key={p.id}>
                    <td>
                      {p.project_title || projectTitleById(p.project)}
                      {pnlRecordStatusBadge(p) === "UPDATED" && (
                        <span className="badge badge-UPDATED" style={{ marginLeft: 8 }}>Updated</span>
                      )}
                    </td>
                    <td>{p.period_start} to {p.period_end}</td>
                    <td>BDT {money(p.revenue)}</td>
                    <td>BDT {money(p.expenses)}</td>
                    <td>BDT {money(p.admin_fee)}</td>
                    <td>BDT {money(p.net_pnl)}</td>
                    <td>BDT {money(p.updated_fund_value)}</td>
                    <td>{p.submitted_at ? new Date(p.submitted_at).toLocaleString() : "—"}</td>
                    <td>{p.submitted_by_name || "—"}</td>
                    <td>
                      <span style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button type="button" onClick={() => runAdminAction(() => reviewAdminPnl(p.id, { status: "APPROVED" }), "PnL approved.", "Failed to approve PnL.")}>Approve</button>
                        <button type="button" className="btn-danger" onClick={() => openRejectModal("pnl_reject", p.id, "Reject PnL submission")}>Reject</button>
                      </span>
                    </td>
                  </tr>
                ))}
                {pendingPnl.length === 0 && (
                  <tr>
                    <td colSpan={10} className="muted">No pending P&amp;L submissions.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <PaginationControls page={pages.pnl} total={pendingPnl.length} pageSize={10} onPageChange={(v) => setPage("pnl", v)} />
        </div>

        <div className="card" style={{ padding: 14 }}>
          <h3>P&amp;L history (by project)</h3>
          <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
            All asset manager P&amp;L records — approved, rejected, and pending — with full revenue, expenses, and fund value.
          </p>
          <div style={{ marginBottom: 12, maxWidth: 360 }}>
            <label className="muted" style={{ display: "block", fontSize: 12, marginBottom: 4 }} htmlFor="admin-pnl-history-project">
              Filter by project
            </label>
            <select
              id="admin-pnl-history-project"
              className="compact-field"
              value={pnlHistoryProjectFilter}
              onChange={(e) => {
                setPnlHistoryProjectFilter(e.target.value);
                setPage("pnlHistory", 1);
              }}
            >
              <option value="">All projects with P&amp;L</option>
              {pnlProjectOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {projectTitleWithCode(p)}
                </option>
              ))}
            </select>
          </div>
          <div className="table-wrap">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Period</th>
                  <th>Revenue</th>
                  <th>Expenses</th>
                  <th>Admin fee</th>
                  <th>Net P&amp;L</th>
                  <th>Fund after P&amp;L</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th>Asset manager</th>
                </tr>
              </thead>
              <tbody>
                {paginate(pnlHistoryRows, pages.pnlHistory, 10).map((p) => (
                  <tr key={`hist-${p.id}`}>
                    <td>{p.project_title || projectTitleById(p.project)}</td>
                    <td>{p.period_start} to {p.period_end}</td>
                    <td>BDT {money(p.revenue)}</td>
                    <td>BDT {money(p.expenses)}</td>
                    <td>BDT {money(p.admin_fee)}</td>
                    <td>BDT {money(p.net_pnl)}</td>
                    <td>BDT {money(p.updated_fund_value)}</td>
                    <td>{p.submitted_at ? new Date(p.submitted_at).toLocaleString() : "—"}</td>
                    <td><span className={`badge badge-${pnlRecordStatusBadge(p)}`}>{pnlRecordStatusLabel(p)}</span></td>
                    <td>{p.submitted_by_name || "—"}</td>
                  </tr>
                ))}
                {pnlHistoryRows.length === 0 && (
                  <tr>
                    <td colSpan={10} className="muted">No P&amp;L records yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <PaginationControls
            page={pages.pnlHistory}
            total={pnlHistoryRows.length}
            pageSize={10}
            onPageChange={(v) => setPage("pnlHistory", v)}
          />
          <p className="muted" style={{ marginTop: 14, marginBottom: 0, fontSize: 13 }}>
            Investor <strong>redemptions</strong> (sell to treasury) and pending <strong>secondary transfers</strong> are reviewed on the{" "}
            {hasAdminModuleAccess(user, "OPERATIONS") ? (
              <Link to="/admin/control?tab=REDEMPTIONS">Redemptions</Link>
            ) : (
              <strong>Redemptions</strong>
            )}{" "}
            tab
            {pendingRedemptionCountOverview > 0 ? (
              <>
                {" "}
                (<strong>{pendingRedemptionCountOverview}</strong> pending redemption{pendingRedemptionCountOverview !== 1 ? "s" : ""})
              </>
            ) : null}
            .
          </p>
        </div>
      </div>
      )}

      {activeTab === "REDEMPTIONS" && (
      <div className="stack">
        <div className="card" style={{ padding: 14 }}>
          <h3>Redemption Requests &amp; History</h3>
          <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
            Pending <strong>redemptions</strong> use the same investor NAV as the project card (approved PNL, fund value,
            price history). <strong>Transfers</strong> keep the locked NAV from when the offer was sent until settlement.
            Fulfilled or rejected redemptions are listed below for audit and reference.
          </p>
          <h4 style={{ marginTop: 14, marginBottom: 8 }}>Pending redemption requests</h4>
          <div className="table-wrap">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Investor</th>
                  <th>Project</th>
                  <th>Type</th>
                  <th>Ref</th>
                  <th>Shares</th>
                  <th>NAV / sh (BDT)</th>
                  <th>Gross (BDT)</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingRedemptionRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="muted">
                      No pending redemption requests.
                    </td>
                  </tr>
                ) : (
                  paginate(pendingRedemptionRows, pages.investments, 10).map((i) => (
                    <tr key={i.id}>
                      <td>{i.investor_display || "—"}</td>
                      <td>{i.project_display || projectTitleById(i.project)}</td>
                      <td>{i.type}</td>
                      <td>{i.redemption_ref || "—"}</td>
                      <td>{i.shares}</td>
                      <td>{money(adminInvestmentNavBdt(i))}</td>
                      <td>{money(pendingInvestmentGrossBdt(i))}</td>
                      <td>
                        <span style={{ display: "flex", gap: 8 }}>
                          <button
                            type="button"
                            onClick={() =>
                              setRedemptionApproveModal({
                                open: true,
                                id: i.id,
                                ref: i.redemption_ref || "",
                                shares: String(i.shares || ""),
                                approved_shares: String(i.shares || ""),
                                admin_notes: "",
                                submitting: false,
                              })
                            }
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="btn-danger"
                            onClick={() => openRejectModal("investment_reject", i.id, "Reject request", [], i.type)}
                          >
                            Reject
                          </button>
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <PaginationControls
            page={pages.investments}
            total={pendingRedemptionRows.length}
            pageSize={10}
            onPageChange={(v) => setPage("investments", v)}
          />

          <h4 style={{ marginTop: 18, marginBottom: 8 }}>Past redemptions</h4>
          <div className="table-wrap">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Investor</th>
                  <th>Project</th>
                  <th>Ref</th>
                  <th>Shares</th>
                  <th>NAV / sh (BDT)</th>
                  <th>Gross (BDT)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pastRedemptionRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="muted">
                      No past redemptions yet.
                    </td>
                  </tr>
                ) : (
                  paginate(pastRedemptionRows, pages.pastRedemptions, 10).map((i) => (
                    <tr key={i.id}>
                      <td>{i.investor_display || "—"}</td>
                      <td>{i.project_display || projectTitleById(i.project)}</td>
                      <td>{i.redemption_ref || "—"}</td>
                      <td>{i.shares}</td>
                      <td>{money(adminInvestmentNavBdt(i))}</td>
                      <td>{money(pendingInvestmentGrossBdt(i))}</td>
                      <td>
                        <span className={`badge badge-${i.status}`}>{statusLabel(i.status)}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <PaginationControls
            page={pages.pastRedemptions}
            total={pastRedemptionRows.length}
            pageSize={10}
            onPageChange={(v) => setPage("pastRedemptions", v)}
          />

          {pendingOtherInvestmentRows.length > 0 && (
            <>
              <h4 style={{ marginTop: 18, marginBottom: 8 }}>Pending transfers &amp; other reviews</h4>
              <div className="table-wrap">
                <table className="ops-table">
                  <thead>
                    <tr>
                      <th>Investor</th>
                      <th>Project</th>
                      <th>Type</th>
                      <th>Ref</th>
                      <th>Shares</th>
                      <th>NAV / sh (BDT)</th>
                      <th>Gross (BDT)</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginate(pendingOtherInvestmentRows, pages.investmentsOther, 10).map((i) => (
                      <tr key={i.id}>
                        <td>{i.investor_display || "—"}</td>
                        <td>{i.project_display || projectTitleById(i.project)}</td>
                        <td>{i.type}</td>
                        <td>{i.redemption_ref || "—"}</td>
                        <td>{i.shares}</td>
                        <td>{money(adminInvestmentNavBdt(i))}</td>
                        <td>{money(pendingInvestmentGrossBdt(i))}</td>
                        <td>
                          <span style={{ display: "flex", gap: 8 }}>
                            <button
                              type="button"
                              onClick={() =>
                                runAdminAction(
                                  () => reviewAdminInvestment(i.id, { status: "APPROVED" }),
                                  "Request approved.",
                                  "Failed to approve request."
                                )
                              }
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              className="btn-danger"
                              onClick={() => openRejectModal("investment_reject", i.id, "Reject request", [], i.type)}
                            >
                              Reject
                            </button>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationControls
                page={pages.investmentsOther}
                total={pendingOtherInvestmentRows.length}
                pageSize={10}
                onPageChange={(v) => setPage("investmentsOther", v)}
              />
            </>
          )}
        </div>
      </div>
      )}

      {activeTab === "NOTICE" && (
      <form className="card stack" style={{ padding: 14 }} onSubmit={handleNoticeBroadcast}>
        <h3>Notice Management</h3>
        <input
          placeholder="Notice title"
          value={noticeForm.title}
          onChange={(e) => setNoticeForm({ ...noticeForm, title: e.target.value })}
          required
        />
        <textarea
          placeholder="Broadcast message"
          value={noticeForm.message}
          onChange={(e) => setNoticeForm({ ...noticeForm, message: e.target.value })}
          required
        />
        <div className="grid-2">
          <select value={noticeForm.audience} onChange={(e) => setNoticeForm({ ...noticeForm, audience: e.target.value })}>
            <option value="INVESTORS">Investors</option>
            <option value="ALL_USERS">All Users</option>
          </select>
          <select value={noticeForm.channel} onChange={(e) => setNoticeForm({ ...noticeForm, channel: e.target.value })}>
            <option value="IN_APP">In App</option>
            <option value="EMAIL">Email</option>
          </select>
        </div>
        <button type="submit">Broadcast Notice</button>
      </form>
      )}

      {permissionModal.open && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>Update Permissions</h3>
            <p className="modal-sub">
              Choose admin modules for <strong>{permissionModal.userName}</strong>.
            </p>
            <div className="stack">
              {ADMIN_PERMISSION_OPTIONS.map((opt) => (
                <label key={opt.key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    style={{ width: 16, height: 16 }}
                    checked={permissionModal.permissions.includes(opt.key)}
                    onChange={() => togglePermission(opt.key)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setPermissionModal({ open: false, userId: "", userName: "", permissions: [] })}
              >
                Cancel
              </button>
              <button type="button" onClick={saveUserPermissions}>Save</button>
            </div>
          </div>
        </div>
      )}

      {redemptionApproveModal.open && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>Approve redemption {redemptionApproveModal.ref || ""}</h3>
            <p className="modal-sub">Requested {redemptionApproveModal.shares} sh.</p>
            <input
              className="compact-field"
              type="number"
              min="0.01"
              step="0.01"
              value={redemptionApproveModal.approved_shares}
              onChange={(e) => setRedemptionApproveModal((m) => ({ ...m, approved_shares: e.target.value }))}
            />
            <textarea
              value={redemptionApproveModal.admin_notes}
              onChange={(e) => setRedemptionApproveModal((m) => ({ ...m, admin_notes: e.target.value }))}
              placeholder="Admin notes (optional)"
            />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button type="button" className="btn-secondary" onClick={() => setRedemptionApproveModal({ open: false, id: "", ref: "", shares: "", approved_shares: "", admin_notes: "", submitting: false })}>
                Cancel
              </button>
              <button
                type="button"
                disabled={redemptionApproveModal.submitting}
                onClick={() => {
                  const requested = Number(redemptionApproveModal.shares);
                  const approved = Number(redemptionApproveModal.approved_shares);
                  if (!Number.isFinite(approved) || approved <= 0 || approved > requested) {
                    setMessage("Invalid approved shares.");
                    return;
                  }
                  const payload = { status: "APPROVED", admin_notes: redemptionApproveModal.admin_notes || "" };
                  if (approved < requested) payload.approved_shares = approved;
                  runAdminAction(() => reviewAdminRedemption(redemptionApproveModal.id, payload), "Redemption approved.", "Failed.").finally(() =>
                    setRedemptionApproveModal({ open: false, id: "", ref: "", shares: "", approved_shares: "", admin_notes: "", submitting: false })
                  );
                }}
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectModal.open && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>{rejectModal.title}</h3>
            <p className="modal-sub">Provide a reason for audit and user visibility.</p>
            <textarea
              value={rejectModal.reason}
              onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
              placeholder="Enter rejection reason"
            />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                className="btn-secondary"
                onClick={() =>
                  setRejectModal({
                    open: false,
                    kind: "",
                    id: "",
                    depositIds: [],
                    investmentTxType: "",
                    reason: "",
                    title: "",
                  })
                }
              >
                Cancel
              </button>
              <button className="btn-danger" onClick={submitReject}>Submit</button>
            </div>
          </div>
        </div>
      )}

      <DocumentPreviewModal
        open={depositDocPreview.open}
        url={depositDocPreview.url}
        title={depositDocPreview.title}
        subtitle={depositDocPreview.subtitle}
        loading={depositDocPreview.loading}
        error={depositDocPreview.error}
        authRequired={depositDocPreview.authRequired}
        contentHint={depositDocPreview.contentHint}
        onClose={closeDepositDocPreview}
      />

      {depositApproveModal.open && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>Approve deposit</h3>
            <p className="modal-sub">
              {depositApproveModal.kind === "bulk"
                ? `You are about to approve ${depositApproveModal.depositIds.length} deposit request(s). The same note will be stored on each approval.`
                : "Confirm crediting the investor wallet for this deposit."}
              {depositApproveModal.amountLabel ? (
                <>
                  {" "}
                  <strong>{depositApproveModal.amountLabel}</strong>
                </>
              ) : null}
            </p>
            <label className="muted" style={{ fontSize: 13, display: "block", marginBottom: 6 }}>
              Admin remark
              {settings?.deposit_approval_remark_required ? <span> (required)</span> : <span className="muted"> (optional)</span>}
            </label>
            <textarea
              rows={4}
              maxLength={1000}
              value={depositApproveModal.remark}
              onChange={(e) => setDepositApproveModal({ ...depositApproveModal, remark: e.target.value })}
              placeholder="Enter approval remarks, payment verification notes, or additional instructions..."
              disabled={depositApproveModal.submitting}
            />
            <p className="muted" style={{ fontSize: 12, margin: "4px 0 0" }}>
              {depositApproveModal.remark.trim().length}/1000 characters
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <button
                type="button"
                className="btn-secondary"
                disabled={depositApproveModal.submitting}
                onClick={() =>
                  setDepositApproveModal({
                    open: false,
                    kind: "",
                    id: "",
                    depositIds: [],
                    amountLabel: "",
                    remark: "",
                    submitting: false,
                  })
                }
              >
                Cancel
              </button>
              <button type="button" disabled={depositApproveModal.submitting} onClick={submitDepositApprove}>
                {depositApproveModal.submitting ? "Submitting…" : "Confirm approval"}
              </button>
            </div>
          </div>
        </div>
      )}

      {projectModalOpen && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: 640, maxHeight: "92vh", overflow: "auto" }}>
            <h3>Create Project</h3>
            <p className="modal-sub">
              Fields follow the public landing page order: banner → name &amp; address → offer facts → profit →
              description → media → risk → publish.
            </p>
            <form className="stack admin-project-form" onSubmit={onCreateProject} noValidate>
              {projectCreateError ? (
                <p className="msg msg-error" role="alert" style={{ marginTop: 0 }}>
                  {projectCreateError}
                </p>
              ) : null}

              <section className="admin-project-form__section">
                <h4>1. Hero &amp; identity</h4>
                <p className="admin-project-form__hint">Banner image, then the name and address investors see first.</p>
                <label>Hero banner image (JPEG / PNG / WebP)</label>
                <input
                  className="compact-field"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => {
                    createProjectHeroFileRef.current = e.target.files?.[0] || null;
                  }}
                />
                <label>Project name *</label>
                <input
                  className="compact-field"
                  placeholder="e.g. North City Diagnostic Center"
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                />
                <label>Project address *</label>
                <input
                  className="compact-field"
                  placeholder="e.g. House 12, Road 5, Dhanmondi, Dhaka"
                  value={newProject.address}
                  onChange={(e) => setNewProject({ ...newProject, address: e.target.value })}
                />
                <label>Category</label>
                <select
                  className="compact-field"
                  value={newProject.category}
                  onChange={(e) => setNewProject({ ...newProject, category: e.target.value })}
                >
                  <option value="CLINIC">Clinic</option>
                  <option value="HOSPITAL">Hospital</option>
                  <option value="DIAGNOSTIC">Diagnostic Centre</option>
                  <option value="REAL_ESTATE">Real Estate</option>
                  <option value="OTHER">Others</option>
                </select>
                {newProject.category === "OTHER" ? (
                  <>
                    <label>Custom category name *</label>
                    <input
                      className="compact-field"
                      placeholder="Enter custom category name"
                      value={customProjectCategory}
                      onChange={(e) => setCustomProjectCategory(e.target.value)}
                    />
                  </>
                ) : null}
              </section>

              <section className="admin-project-form__section">
                <h4>2. Offer facts</h4>
                <p className="admin-project-form__hint">
                  Shares, price, and selling window. New projects start as Draft until you publish.
                </p>
                <label>Total shares *</label>
                <input
                  className="compact-field"
                  type="number"
                  min="1"
                  placeholder="e.g. 1000"
                  value={newProject.total_shares}
                  onChange={(e) => setNewProject({ ...newProject, total_shares: e.target.value })}
                />
                <label>Price per share (BDT) *</label>
                <input
                  className="compact-field"
                  type="number"
                  min="1"
                  placeholder="e.g. 500"
                  value={newProject.initial_share_price}
                  onChange={(e) => setNewProject({ ...newProject, initial_share_price: e.target.value })}
                />
                <label>Share selling start date *</label>
                <input
                  className="compact-field"
                  type="date"
                  value={newProject.fundraising_start}
                  onChange={(e) => setNewProject({ ...newProject, fundraising_start: e.target.value })}
                />
                <label>Share selling end date *</label>
                <input
                  className="compact-field"
                  type="date"
                  value={newProject.fundraising_end}
                  min={newProject.fundraising_start}
                  onChange={(e) => setNewProject({ ...newProject, fundraising_end: e.target.value })}
                />
                <p className="muted" style={{ margin: "4px 0 0", fontSize: 13 }}>
                  Lock-in after full funding uses the platform default (
                  {settings?.default_lockin_months != null
                    ? `${settings.default_lockin_months} month(s)`
                    : "see Settings → Default lock-in months"}
                  ).
                </p>
              </section>

              <section className="admin-project-form__section">
                <h4>3. Estimated profit</h4>
                <label>Estimated return % per annum *</label>
                <input
                  className="compact-field"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g. 12.50"
                  value={newProject.expected_return_pct}
                  onChange={(e) => setNewProject({ ...newProject, expected_return_pct: e.target.value })}
                />
              </section>

              <section className="admin-project-form__section">
                <h4>4. Project description</h4>
                <p className="admin-project-form__hint">At least three short paragraphs for the landing story.</p>
                <label>Paragraph 1 *</label>
                <textarea
                  className="compact-field"
                  rows={3}
                  placeholder="What the project is and where it operates"
                  value={newProject.description_p1}
                  onChange={(e) => setNewProject({ ...newProject, description_p1: e.target.value })}
                />
                <label>Paragraph 2 *</label>
                <textarea
                  className="compact-field"
                  rows={3}
                  placeholder="Use of funds / what investors are funding"
                  value={newProject.description_p2}
                  onChange={(e) => setNewProject({ ...newProject, description_p2: e.target.value })}
                />
                <label>Paragraph 3 *</label>
                <textarea
                  className="compact-field"
                  rows={3}
                  placeholder="Timeline, milestones, or expected outcomes"
                  value={newProject.description_p3}
                  onChange={(e) => setNewProject({ ...newProject, description_p3: e.target.value })}
                />
              </section>

              <section className="admin-project-form__section">
                <h4>5. Media (optional)</h4>
                <p className="admin-project-form__hint">Shown on the landing page only when provided.</p>
                <label>Project overview PDF</label>
                <input
                  className="compact-field"
                  type="file"
                  accept="application/pdf"
                  onChange={(e) =>
                    uploadProjectOverviewPdf(e.target.files?.[0], async (key) => {
                      createProjectOverviewKeyRef.current = key;
                      setNewProject((prev) => ({ ...prev, overview_pdf_url: key }));
                      setMessage("Project overview PDF uploaded.");
                    })
                  }
                  disabled={projectOverviewUploadBusy}
                />
                {newProject.overview_pdf_url ? (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <p className="muted" style={{ marginTop: -4, marginBottom: 0 }}>
                      PDF ready
                    </p>
                    <button
                      type="button"
                      className="btn-secondary compact-btn"
                      onClick={() => openProjectOverviewPdfByKey(newProject.overview_pdf_url)}
                      disabled={projectOverviewDeleteBusy}
                    >
                      Review PDF
                    </button>
                    <button
                      type="button"
                      className="btn-danger compact-btn"
                      onClick={() =>
                        confirmAndRemoveProjectOverviewPdf({
                          key: newProject.overview_pdf_url,
                          onRemoved: () => {
                            createProjectOverviewKeyRef.current = "";
                            setNewProject((prev) => ({ ...prev, overview_pdf_url: "" }));
                          },
                        })
                      }
                      disabled={projectOverviewDeleteBusy || projectOverviewUploadBusy || projectOverviewAttachBusy}
                    >
                      {projectOverviewDeleteBusy ? "Deleting..." : "Delete PDF"}
                    </button>
                  </div>
                ) : null}
                {projectOverviewUploadBusy ? (
                  <p className="muted" style={{ marginTop: -2 }}>
                    Uploading PDF…
                  </p>
                ) : null}
                <label>Project video URL (YouTube / Vimeo)</label>
                <input
                  className="compact-field"
                  type="url"
                  placeholder="https://www.youtube.com/watch?v=…"
                  value={newProject.video_url || ""}
                  onChange={(e) => setNewProject({ ...newProject, video_url: e.target.value })}
                />
              </section>

              <section className="admin-project-form__section">
                <h4>6. Risk &amp; publish</h4>
                <label>Risk disclosure *</label>
                <textarea
                  className="compact-field"
                  rows={3}
                  placeholder="Clear risk language shown above the Buy Shares button"
                  value={newProject.risk_disclaimer}
                  onChange={(e) => setNewProject({ ...newProject, risk_disclaimer: e.target.value })}
                />
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(newProject.landing_published)}
                    onChange={(e) => setNewProject({ ...newProject, landing_published: e.target.checked })}
                  />
                  Publish landing page now (public share URL `/p/...`)
                </label>
              </section>

              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button type="button" className="btn-secondary compact-btn" onClick={closeCreateProjectModal}>
                  Cancel
                </button>
                <button
                  className="compact-btn"
                  type="submit"
                  disabled={
                    projectCreateSubmitting ||
                    projectOverviewUploadBusy ||
                    projectOverviewDeleteBusy ||
                    projectOverviewAttachBusy
                  }
                >
                  {projectCreateSubmitting
                    ? "Creating…"
                    : projectOverviewUploadBusy
                      ? "Uploading PDF..."
                      : projectOverviewDeleteBusy
                        ? "Deleting PDF..."
                        : projectOverviewAttachBusy
                          ? "Attaching PDF..."
                          : "Create Project & Landing"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {projectEditModal.open && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: 640, maxHeight: "92vh", overflow: "auto" }}>
            <h3>Edit project &amp; landing</h3>
            <p className="modal-sub">
              Same sections as Create Project. Share price and total shares lock after the first sale; dates lock after funding.
              About, landing, and documents can still be updated.
            </p>
            {projectEditModal.lifecycleLocked ? (
              <p className="msg" style={{ fontSize: 13 }}>
                Share cap, pricing, and fundraising dates cannot be changed here — use{" "}
                <strong>Extend 7 Days</strong> on the project row for schedule adjustments.
              </p>
            ) : projectEditModal.offerTermsLocked ? (
              <p className="msg" style={{ fontSize: 13 }}>
                Share price and total shares are locked after the first investment. You can still update about text, PDF,
                landing, and fundraising dates. Emergency unwind: Super Admin{" "}
                <strong>Cancel project</strong> (100% purchase-price refund).
              </p>
            ) : null}
            {projectEditModal.supplyProject ? (
              <div className="msg" style={{ fontSize: 13, marginBottom: 12 }}>
                <strong>Share cap snapshot</strong>
                <div style={{ marginTop: 6 }}>
                  <AdminShareSupplySummary project={projectEditModal.supplyProject} />
                </div>
              </div>
            ) : null}

            <div className="admin-project-form">
            <section className="admin-project-form__section">
              <h4>1. Hero &amp; identity</h4>
              <label>Hero banner image (JPEG / PNG / WebP)</label>
              <input
                className="compact-field"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => onLandingHeroFile(e.target.files?.[0], projectEditModal.projectId)}
              />
              {projectEditModal.form.landing_hero_image_url ? (
                <p style={{ fontSize: 12, margin: "6px 0" }}>
                  Hero on file.{" "}
                  <button
                    type="button"
                    className="btn-secondary compact-btn"
                    onClick={() => onLandingHeroDelete(projectEditModal.projectId)}
                  >
                    Remove
                  </button>
                </p>
              ) : null}
              <label>Project name *</label>
              <input
                className="compact-field"
                placeholder="Project name"
                value={projectEditModal.form.name}
                onChange={(e) => setProjectEditModal((prev) => ({ ...prev, form: { ...prev.form, name: e.target.value } }))}
              />
              <label>Project address *</label>
              <input
                className="compact-field"
                placeholder="e.g. House 12, Road 5, Dhanmondi, Dhaka"
                value={projectEditModal.form.address || ""}
                onChange={(e) => setProjectEditModal((prev) => ({ ...prev, form: { ...prev.form, address: e.target.value } }))}
              />
              <label>Category</label>
              <select
                className="compact-field"
                value={projectEditModal.form.category}
                onChange={(e) => setProjectEditModal((prev) => ({ ...prev, form: { ...prev.form, category: e.target.value } }))}
              >
                <option value="CLINIC">Clinic</option>
                <option value="HOSPITAL">Hospital</option>
                <option value="DIAGNOSTIC">Diagnostic Centre</option>
                <option value="REAL_ESTATE">Real Estate</option>
                <option value="OTHER">Others</option>
              </select>
            </section>

            <section className="admin-project-form__section">
              <h4>2. Offer facts</h4>
              <label>Total shares</label>
              <input
                className="compact-field"
                type="number"
                min="1"
                disabled={projectEditModal.offerTermsLocked || projectEditModal.lifecycleLocked}
                value={projectEditModal.form.total_shares}
                onChange={(e) => setProjectEditModal((prev) => ({ ...prev, form: { ...prev.form, total_shares: e.target.value } }))}
              />
              <label>Price per share (BDT)</label>
              <input
                className="compact-field"
                type="number"
                min="1"
                disabled={projectEditModal.offerTermsLocked || projectEditModal.lifecycleLocked}
                value={projectEditModal.form.initial_share_price}
                onChange={(e) => setProjectEditModal((prev) => ({ ...prev, form: { ...prev.form, initial_share_price: e.target.value } }))}
              />
              <label>Share selling start date</label>
              <input
                className="compact-field"
                type="date"
                disabled={projectEditModal.lifecycleLocked}
                value={projectEditModal.form.fundraising_start}
                onChange={(e) => setProjectEditModal((prev) => ({ ...prev, form: { ...prev.form, fundraising_start: e.target.value } }))}
              />
              <label>Share selling end date</label>
              <input
                className="compact-field"
                type="date"
                disabled={projectEditModal.lifecycleLocked}
                value={projectEditModal.form.fundraising_end}
                onChange={(e) => setProjectEditModal((prev) => ({ ...prev, form: { ...prev.form, fundraising_end: e.target.value } }))}
              />
            </section>

            <section className="admin-project-form__section">
              <h4>3. Estimated profit</h4>
              <label>Estimated return % per annum</label>
              <input
                className="compact-field"
                type="number"
                min="0"
                step="0.01"
                value={projectEditModal.form.expected_return_pct}
                onChange={(e) => setProjectEditModal((prev) => ({ ...prev, form: { ...prev.form, expected_return_pct: e.target.value } }))}
              />
            </section>

            <section className="admin-project-form__section">
              <h4>4. Project description</h4>
              <label>Paragraph 1 *</label>
              <textarea
                className="compact-field"
                rows={3}
                value={projectEditModal.form.description_p1 || ""}
                onChange={(e) => setProjectEditModal((prev) => ({ ...prev, form: { ...prev.form, description_p1: e.target.value } }))}
              />
              <label>Paragraph 2 *</label>
              <textarea
                className="compact-field"
                rows={3}
                value={projectEditModal.form.description_p2 || ""}
                onChange={(e) => setProjectEditModal((prev) => ({ ...prev, form: { ...prev.form, description_p2: e.target.value } }))}
              />
              <label>Paragraph 3 *</label>
              <textarea
                className="compact-field"
                rows={3}
                value={projectEditModal.form.description_p3 || ""}
                onChange={(e) => setProjectEditModal((prev) => ({ ...prev, form: { ...prev.form, description_p3: e.target.value } }))}
              />
            </section>

            <section className="admin-project-form__section">
              <h4>5. Media</h4>
              <label>Project video URL (YouTube / Vimeo)</label>
              <input
                className="compact-field"
                type="url"
                placeholder="https://www.youtube.com/watch?v=…"
                value={projectEditModal.form.video_url || ""}
                onChange={(e) => setProjectEditModal((prev) => ({ ...prev, form: { ...prev.form, video_url: e.target.value } }))}
              />
            </section>

            <section className="admin-project-form__section">
              <h4>6. Risk &amp; publish</h4>
              <label>Risk disclosure *</label>
              <textarea
                className="compact-field"
                rows={3}
                value={projectEditModal.form.risk_disclaimer}
                onChange={(e) => setProjectEditModal((prev) => ({ ...prev, form: { ...prev.form, risk_disclaimer: e.target.value } }))}
              />
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={Boolean(projectEditModal.form.landing_published)}
                  onChange={(e) =>
                    setProjectEditModal((prev) => ({
                      ...prev,
                      form: { ...prev.form, landing_published: e.target.checked },
                    }))
                  }
                />
                Publish landing page (public share URL `/p/...`)
              </label>
              {projectEditModal.form.landing_slug ? (
                <p className="muted" style={{ fontSize: 13 }}>
                  Share link:{" "}
                  <a href={`/p/${projectEditModal.form.landing_slug}`} target="_blank" rel="noopener noreferrer">
                    {`${typeof window !== "undefined" ? window.location.origin : ""}/p/${projectEditModal.form.landing_slug}`}
                  </a>
                </p>
              ) : (
                <p className="muted" style={{ fontSize: 13 }}>Publish and save to generate a share URL.</p>
              )}
            </section>

            <section className="admin-project-form__section">
            <h4>Project PDF</h4>
            <label style={{ fontSize: 13, color: "var(--text-muted)" }}>
              {projectEditModal.pdfUpdateOnly ? "Performance / update PDF" : "Project overview PDF"}
            </label>
            <p className="muted" style={{ marginTop: -6, marginBottom: 0, fontSize: 12 }}>
              The current project PDF remains attached across all project stage changes unless you replace or delete it.
            </p>
            <p className="muted" style={{ marginTop: -2, marginBottom: 0, fontSize: 12 }}>
              Saved on project:{" "}
              <strong>{String(projectEditModal.form.overview_pdf_url || "").trim() ? "Yes" : "No"}</strong>
              {String(projectEditModal.form.overview_pdf_url || "").trim()
                ? ` (${String(projectEditModal.form.overview_pdf_url || "").trim()})`
                : ""}
            </p>
            {(!projectEditModal.readOnly || projectEditModal.pdfUpdateOnly) ? (
            <p className="muted" style={{ marginTop: -2, marginBottom: 0, fontSize: 12 }}>
              {projectEditModal.pdfUpdateOnly
                ? "Choose a PDF to publish performance updates to investors. Upload saves immediately."
                : 'Note: file chooser always shows "No file chosen" after reopen by browser security design.'}
            </p>
            ) : null}
            {(!projectEditModal.readOnly || projectEditModal.pdfUpdateOnly) ? (
            <input
              className="compact-field"
              type="file"
              accept="application/pdf"
              onChange={async (e) => {
                const file = e.target.files?.[0] || null;
                setProjectEditPendingPdfFile(file);
                if (file) {
                  const pid = String(projectEditModal.projectId || "").trim();
                  if (!pid) {
                    setMessage("Missing project ID for PDF upload.");
                    return;
                  }
                  setProjectOverviewAttachBusy(true);
                  setProjectEditPdfUploadProgress(0);
                  try {
                    const formData = new FormData();
                    formData.append("file", file);
                    const { data } = await uploadAdminProjectOverviewPdfToProject(pid, formData, {
                      onUploadProgress: (evt) => {
                        const total = Number(evt?.total || 0);
                        const loaded = Number(evt?.loaded || 0);
                        if (total > 0) {
                          // Keep <=95 until server confirms persistence; 100 means success.
                          setProjectEditPdfUploadProgress(Math.min(95, Math.round((loaded / total) * 100)));
                        }
                      },
                    });
                    const uploadedKey = String(data?.overview_pdf_url || "").trim();
                    if (!uploadedKey) {
                      setMessage("Could not attach selected PDF to the project.");
                      return;
                    }
                    editProjectOverviewKeyRef.current = uploadedKey;
                    setProjectEditModal((prev) => ({ ...prev, form: { ...prev.form, overview_pdf_url: uploadedKey } }));
                    setProjectEditPdfUploadProgress(100);
                    setMessage(
                      projectEditModal.pdfUpdateOnly
                        ? "Performance PDF uploaded. Investors can view it on the project."
                        : "Project overview PDF uploaded and attached to the project."
                    );
                    await load();
                  } catch (err) {
                    setProjectEditPdfUploadProgress(0);
                    setMessage(getErrorMessage(err, "Could not upload selected PDF to the project."));
                  } finally {
                    setProjectOverviewAttachBusy(false);
                    setProjectEditPendingPdfFile(null);
                    e.target.value = "";
                  }
                }
              }}
              disabled={projectOverviewUploadBusy || projectOverviewAttachBusy}
            />
            ) : null}
            {(!projectEditModal.readOnly || projectEditModal.pdfUpdateOnly) && projectEditPendingPdfFile ? (
              <p className="muted" style={{ marginTop: -2 }}>
                Selected for upload: {projectEditPendingPdfFile.name}
              </p>
            ) : null}
            {(!projectEditModal.readOnly || projectEditModal.pdfUpdateOnly) && projectEditPdfUploadProgress != null ? (
              <div style={{ marginTop: 4 }}>
                <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
                  PDF upload progress: {projectEditPdfUploadProgress}%
                </div>
                <div
                  style={{
                    width: "100%",
                    height: 8,
                    borderRadius: 999,
                    background: "#e5e7eb",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${projectEditPdfUploadProgress}%`,
                      height: "100%",
                      background: projectEditPdfUploadProgress >= 100 ? "var(--brand-green, #006a4e)" : "var(--brand-green-hover, #005c44)",
                      transition: "width 0.2s ease",
                    }}
                  />
                </div>
              </div>
            ) : null}
            {projectEditModal.form.overview_pdf_url && (
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  className="compact-field"
                  value={projectEditModal.form.overview_pdf_url}
                  readOnly
                />
                <button
                  className="btn-secondary"
                  type="button"
                  onClick={() => openProjectOverviewPdfByKey(projectEditModal.form.overview_pdf_url)}
                  disabled={projectOverviewDeleteBusy}
                >
                  Open PDF
                </button>
                {(!projectEditModal.readOnly || projectEditModal.pdfUpdateOnly) ? (
                <button
                  className="btn-danger"
                  type="button"
                  onClick={() =>
                    confirmAndRemoveProjectOverviewPdf({
                      key: projectEditModal.form.overview_pdf_url,
                      projectId: projectEditModal.projectId,
                      onRemoved: () => {
                        editProjectOverviewKeyRef.current = "";
                        setProjectEditModal((prev) => ({ ...prev, form: { ...prev.form, overview_pdf_url: "" } }));
                      },
                    })
                  }
                  disabled={projectOverviewDeleteBusy || projectOverviewUploadBusy || projectOverviewAttachBusy}
                >
                  {projectOverviewDeleteBusy ? "Deleting..." : "Delete PDF"}
                </button>
                ) : null}
              </div>
            )}
            {(!projectEditModal.readOnly || projectEditModal.pdfUpdateOnly) && projectOverviewUploadBusy ? (
              <p className="muted" style={{ marginTop: -2 }}>
                Uploading PDF... Please wait before saving changes.
              </p>
            ) : null}
            {(!projectEditModal.readOnly || projectEditModal.pdfUpdateOnly) && projectOverviewDeleteBusy ? (
              <p className="muted" style={{ marginTop: -2 }}>
                Deleting existing PDF... You can upload a replacement after this completes.
              </p>
            ) : null}
            {(!projectEditModal.readOnly || projectEditModal.pdfUpdateOnly) && projectOverviewAttachBusy ? (
              <p className="muted" style={{ marginTop: -2 }}>
                Attaching PDF to project... Please wait.
              </p>
            ) : null}
            </section>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                className="btn-secondary"
                onClick={() => {
                  editProjectOverviewKeyRef.current = "";
                  setProjectEditPendingPdfFile(null);
                  setProjectEditModal((prev) => ({
                    ...prev,
                    open: false,
                    projectId: "",
                    lifecycleLocked: false,
                    offerTermsLocked: false,
                    readOnly: false,
                    pdfUpdateOnly: false,
                  }));
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveProjectEdits}
                disabled={projectOverviewUploadBusy || projectOverviewDeleteBusy || projectOverviewAttachBusy}
              >
                {projectOverviewUploadBusy
                  ? "Uploading PDF..."
                  : projectOverviewDeleteBusy
                    ? "Deleting PDF..."
                    : projectOverviewAttachBusy
                      ? "Attaching PDF..."
                      : "Save project & landing"}
              </button>
            </div>
          </div>
        </div>
      )}
      {projectStageModal.open && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>{projectStageModal.currentStage === "CANCELLED" ? "Restart Cancelled Project" : "Update Project Stage"}</h3>
            <p className="modal-sub">
              {projectStageModal.currentStage === "CANCELLED"
                ? "Return this project to Draft so it can move through Review → Fundraising again. Sold-share counts are reset."
                : "Select the lifecycle stage and confirm to apply immediately."}
            </p>
            <select
              className="compact-field"
              value={projectStageModal.stage}
              onChange={(e) => setProjectStageModal((prev) => ({ ...prev, stage: e.target.value }))}
            >
              {projectStageModal.allowedStages.map((opt) => (
                <option key={opt} value={opt}>
                  {opt === "DRAFT" ? "Draft" : opt === "REVIEW" ? "Review" : opt === "FUNDRAISING" ? "Fundraising" : opt === "FUNDED" ? "Funded" : opt === "CLOSED" ? "Closed" : opt}
                </option>
              ))}
            </select>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn-secondary" onClick={() => setProjectStageModal({ open: false, projectId: "", currentStage: "DRAFT", stage: "DRAFT", allowedStages: [] })}>
                Cancel
              </button>
              <button onClick={saveProjectStage}>Confirm Stage Update</button>
            </div>
          </div>
        </div>
      )}

      {landingModal.open && (
        <div className="modal-backdrop">
          <div className="modal-card" style={{ maxWidth: 560 }}>
            <h3>Project landing page</h3>
            <p className="modal-sub">{landingModal.projectName}</p>
            <label>Hero banner (JPEG / PNG / WebP)</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => onLandingHeroFile(e.target.files?.[0])}
            />
            {landingModal.form.landing_hero_image_url ? (
              <p style={{ fontSize: 12, margin: "6px 0" }}>
                Hero on file.{" "}
                <button type="button" className="btn-secondary compact-btn" onClick={onLandingHeroDelete}>
                  Remove
                </button>
              </p>
            ) : null}
            <input
              className="compact-field"
              placeholder="Headline"
              value={landingModal.form.landing_headline}
              onChange={(e) =>
                setLandingModal((prev) => ({ ...prev, form: { ...prev.form, landing_headline: e.target.value } }))
              }
            />
            <input
              className="compact-field"
              placeholder="Subline"
              value={landingModal.form.landing_subline}
              onChange={(e) =>
                setLandingModal((prev) => ({ ...prev, form: { ...prev.form, landing_subline: e.target.value } }))
              }
            />
            <textarea
              className="compact-field"
              placeholder="Highlights (one per line, max 6)"
              rows={4}
              value={landingModal.form.landing_highlights_text}
              onChange={(e) =>
                setLandingModal((prev) => ({
                  ...prev,
                  form: { ...prev.form, landing_highlights_text: e.target.value },
                }))
              }
            />
            <textarea
              className="compact-field"
              placeholder="Extra terms notes (optional)"
              rows={3}
              value={landingModal.form.landing_terms_notes}
              onChange={(e) =>
                setLandingModal((prev) => ({ ...prev, form: { ...prev.form, landing_terms_notes: e.target.value } }))
              }
            />
            <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "8px 0" }}>
              <input
                type="checkbox"
                checked={Boolean(landingModal.form.landing_published)}
                onChange={(e) =>
                  setLandingModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, landing_published: e.target.checked },
                  }))
                }
              />
              Publish landing page (public share URL)
            </label>
            {landingModal.form.landing_slug ? (
              <p className="muted" style={{ fontSize: 13 }}>
                Share link:{" "}
                <a href={`/p/${landingModal.form.landing_slug}`} target="_blank" rel="noopener noreferrer">
                  {`${window.location.origin}/p/${landingModal.form.landing_slug}`}
                </a>
                {" · "}
                <button
                  type="button"
                  className="btn-secondary compact-btn"
                  onClick={() =>
                    navigator.clipboard?.writeText(`${window.location.origin}/p/${landingModal.form.landing_slug}`)
                  }
                >
                  Copy
                </button>
              </p>
            ) : (
              <p className="muted" style={{ fontSize: 13 }}>Publish once to generate a share URL.</p>
            )}
            <p className="muted" style={{ fontSize: 12 }}>
              Project overview / update PDFs are still managed on Edit / Update. They appear in the landing Documents section.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setLandingModal((prev) => ({ ...prev, open: false }))}
              >
                Cancel
              </button>
              {landingModal.form.landing_slug ? (
                <a
                  className="btn-secondary"
                  href={`/p/${landingModal.form.landing_slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Preview
                </a>
              ) : landingModal.projectId ? (
                <Link className="btn-secondary" to={`/projects/${landingModal.projectId}/about`} target="_blank">
                  Preview
                </Link>
              ) : null}
              <button type="button" onClick={saveLandingModal} disabled={landingModal.saving}>
                {landingModal.saving ? "Saving…" : "Save landing"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
